import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import BottomPanel from "../../pages/Map/BottomPanel";
import { getTaskListAPI, fetchTaskDetails, fetchTracking } from "../../service/mapView";
import { getActivityListByProjectId, getGroupsByDepartment } from "../../service/User";
import { getPermittedActivities } from "../../utils/activityPermissions";
import {
  filterGroupsByUserAccess,
  filterAgentsByCurrentUserGroups,
  currentUserCanUseGroups,
  getCurrentUserType,
  getCurrentUserGroupIds,
  loadGroupsForDepartment,
  getGroupMemberIdSet,
  isTaskVisibleForGroupAccess,
} from "../task/GroupAssignParts";
import AppContext from "../../context/AppContext";
import ReactSelector from "../common/CommonReactSelector";
import { components } from "react-select";
import ReactDOMServer from "react-dom/server";
import { createPortal } from "react-dom";
import {
  Calendar,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  Download,
  List,
  MapPin,
  Navigation,
  Ruler,
  Square,
  Trash2,
  X,
} from "lucide-react";
import { TbWaveSawTool } from "react-icons/tb";
import { FiUserMinus, FiUserPlus } from "react-icons/fi";
import { LuRefreshCw } from "react-icons/lu";
import MapViewToolbar from "./MapViewToolbar";
import DetailPanel from '../../pages/Map/DetailPanel';
import MapTypeControl from "./MapTypeControl";
import { getPermissionByScreenCode } from "../../utils/permissions";
import { useNavigate } from "react-router-dom";
import * as turf from "@turf/turf";
import { createRoot } from "react-dom/client";
import { toast } from "../../utils/toast";
import {
  assignTasks,
  multiassignTasks,
  unassignTask,
  deleteTasks,
  fetchFieldAgentMap,
  fetchFilterExcel,
} from "../../service/Task";
import { fetchMarkerColors } from "../../service/setting";
import DeleteTask from "../suspence/DeleteTask";
import CommonButton from "../common/CommonButton";
import translations from "../common/Translations";
import Modal from "../DownloadModal/Modal";
import Cookies from "js-cookie";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { isAfter, isBefore, isSameDay } from "date-fns";
import Logo from "../../assets/logo.png";
import { Spinner } from "../../Spinner";
import moment from "moment";
import ModalCloseButton from "../common/ModalCloseButton";
import Tooltip from "../common/Tooltip";
import CustomMarkerSettingsModal from "./CustomMarkerSettingsModal";
import MapAccountSearch from "./MapAccountSearch";
import { canReassignTask } from "../../utils/taskReassignRules";

const PERF_DEBUG = process.env.NODE_ENV !== "production";
const perfNow = () =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
const perfLog = (...args) => {
  if (!PERF_DEBUG) return;
  console.debug("[MapViewPerf]", ...args);
};
const combinedIconCache = new Map();
const getBackendErrorMessage = (error) =>
  error?.response?.data?.body?.value ??
  error?.response?.data?.body?.error ??
  error?.response?.data?.message ??
  error?.body?.value ??
  error?.body?.error;

const AzureZoomControl = ({
  showZoomControl = true,
  showDatePanel,
  datePanelOffset,
  showDetailPanel,
  showBottomPanel,
  bottomPanelExpanded,
  drawingEnabled,
  isRtl = false,
}) => {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  const DETAIL_PANEL_WIDTH = 400;
  const SIDE_EDGE_OFFSET = 16;

  useEffect(() => {
    const onZoomEnd = () => {
      setZoom(map.getZoom());
    };

    map.on("zoomend", onZoomEnd);
    return () => map.off("zoomend", onZoomEnd);
  }, [map]);

  useEffect(() => {
    if (!showZoomControl) return undefined;
    const control = L.control({ position: "topleft" });

    control.onAdd = () => {
      const container = L.DomUtil.create("div", "custom-zoom zoom-container");

      container.innerHTML = `
        <button class="custom-btn" id="zoom-in">+</button>
        <div class="custom-zoom-level">${zoom}x</div>
        <button class="custom-btn" id="zoom-out">-</button>
      `;

      L.DomEvent.disableClickPropagation(container);

      container.querySelector("#zoom-in").onclick = () => map.zoomIn();
      container.querySelector("#zoom-out").onclick = () => map.zoomOut();

      return container;
    };

    control.addTo(map);
    return () => control.remove();
  }, [map, zoom, showZoomControl]);

  // Position right-side controls (zoom, map type, draw) below the list/map view buttons; shift left when detail panel is open
  const updateSidePosition = () => {
    const leftPane = document.querySelector(".leaflet-top.leaflet-left");
    const rightPane = document.querySelector(".leaflet-top.leaflet-right");
    if (!leftPane && !rightPane) return;

    const mapElement = document.getElementById("map");
    const trackingAnchor = mapElement?.querySelector(".map-tracking-anchor");
    const mapHeight = mapElement?.clientHeight ?? 0;
    const mapRect = mapElement?.getBoundingClientRect?.();

    const trackingAnchorTopOffset =
      mapRect && trackingAnchor
        ? Math.round(trackingAnchor.getBoundingClientRect().bottom - mapRect.top + 14)
        : 0;
    // Keep controls tightly aligned below the tracking anchor.
    // Use a compact fallback only when anchor geometry is unavailable.
    const compactFallbackTopOffset = showDatePanel ? 100 : 16;
    const baseTopOffset = trackingAnchorTopOffset > 0
      ? trackingAnchorTopOffset
      : compactFallbackTopOffset;

    const setPaneTop = (pane) => {
      if (!pane) return;
      const paneHeight = pane.getBoundingClientRect().height || 0;
      const maxTopOffset = mapHeight > 0 && paneHeight > 0
        ? Math.max(8, mapHeight - paneHeight - 8)
        : baseTopOffset;
      const topOffset = Math.min(baseTopOffset, maxTopOffset);
      pane.style.transition = "top 0.3s cubic-bezier(0.4, 0, 0.2, 1), right 0.3s ease, left 0.3s ease";
      // Use !important to override global .leaflet-top top rules.
      pane.style.setProperty("top", `${topOffset}px`, "important");
    };

    setPaneTop(rightPane);
    // Keep left pane behavior aligned with right for consistent desktop layout.
    setPaneTop(leftPane);

    const edgeOffset = `${SIDE_EDGE_OFFSET}px`;
    const offsetWithPanel = `${DETAIL_PANEL_WIDTH + SIDE_EDGE_OFFSET}px`;

    if (isRtl) {
      if (rightPane) {
        rightPane.style.right = edgeOffset;
        rightPane.style.left = "auto";
      }
      if (leftPane) {
        leftPane.style.left = showDetailPanel ? offsetWithPanel : edgeOffset;
        leftPane.style.right = "auto";
      }
    } else {
      if (leftPane) {
        leftPane.style.left = edgeOffset;
        leftPane.style.right = "auto";
      }
      if (rightPane) {
        rightPane.style.right = showDetailPanel ? offsetWithPanel : edgeOffset;
        rightPane.style.left = "auto";
      }
    }
  };
  useEffect(() => {
    updateSidePosition();

    // Recalculate after layout transitions/control mount updates (bottom panel, draw controls, etc.)
    const timeoutIds = [
      window.setTimeout(updateSidePosition, 0),
      window.setTimeout(updateSidePosition, 180),
      window.setTimeout(updateSidePosition, 360),
    ];

    const mapElement = document.getElementById("map");
    const trackingAnchor = mapElement?.querySelector(".map-tracking-anchor");
    let resizeObserver = null;
    let trackingObserver = null;
    if (mapElement && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        updateSidePosition();
      });
      resizeObserver.observe(mapElement);
    }
    if (trackingAnchor && typeof MutationObserver !== "undefined") {
      trackingObserver = new MutationObserver(() => {
        updateSidePosition();
      });
      trackingObserver.observe(trackingAnchor, {
        attributes: true,
        attributeFilter: ["style", "class"],
        childList: true,
        subtree: true,
      });
    }

    window.addEventListener("resize", updateSidePosition);
    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      if (resizeObserver) resizeObserver.disconnect();
      if (trackingObserver) trackingObserver.disconnect();
      window.removeEventListener("resize", updateSidePosition);
    };
  }, [showDatePanel, datePanelOffset, showDetailPanel, showBottomPanel, bottomPanelExpanded, drawingEnabled, isRtl]);

  return null;
};

const InvalidateMapSize = ({ showBottomPanel, bottomPanelExpanded, showDatePanel, showDetailPanel }) => {
  const map = useMap();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      map.invalidateSize();
    }, 120);

    return () => window.clearTimeout(timer);
  }, [map, showBottomPanel, bottomPanelExpanded, showDatePanel, showDetailPanel]);

  return null;
};

const MobileMapStabilizer = ({ enabled }) => {
  const map = useMap();

  useEffect(() => {
    if (!enabled) return undefined;

    const invalidate = () => {
      window.requestAnimationFrame(() => {
        map.invalidateSize();
      });
    };

    const timers = [
      window.setTimeout(invalidate, 0),
      window.setTimeout(invalidate, 140),
      window.setTimeout(invalidate, 320),
    ];

    const handleViewportChange = () => invalidate();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("orientationchange", handleViewportChange);

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("orientationchange", handleViewportChange);
    };
  }, [map, enabled]);

  return null;
};


const getMarkerColor = (taskStatus, taskColor) =>
  taskColor ||
  (taskStatus === "Active"
    ? "#4379F2" // Blue
    : taskStatus === "In_Progress"
      ? "#FFA500" // Orange
      : taskStatus === "Assigned"
        ? "#F3C623" // Yellow
        : taskStatus === "Completed"
          ? "#6EC207" // Green
          : taskStatus === "On_Hold"
            ? "#FF3333" // Red
            : "#A0A0A0"); // Default color if no match

const getCombinedMarkerIcon = (taskStatus, taskIconType, taskColor) => {
  const cacheKey = `${taskStatus || ""}|${taskIconType || ""}|${taskColor || ""}`;
  if (combinedIconCache.has(cacheKey)) {
    return combinedIconCache.get(cacheKey);
  }

  let statusIconUrl;
  let taskIconHtml;

  const color = getMarkerColor(taskStatus, taskColor);

  statusIconUrl = ReactDOMServer.renderToString(
    <MapPin size={36} color={color} fill={color} />
  );

  switch (taskIconType) {
    case "SUBMISSION":
      taskIconHtml = ReactDOMServer.renderToString(
        <CheckCheck size={20} color="white" />
      );
      break;
    case "NOT_COMPLETED":
      taskIconHtml = ReactDOMServer.renderToString(
        <X size={20} color="white" />
      );
      break;
    default:
      taskIconHtml = ReactDOMServer.renderToString(<span></span>);
  }

  const icon = L.divIcon({
    html: `
      <div style="position: relative;">
        <h1>${statusIconUrl} </h1>
        <div style="position: absolute; top: 0; left: 5px; width: 100%; height: 100%; display: flex; justify-content: center; align-items: center;">
          ${taskIconHtml}
        </div>
      </div>
    `,
    className: "custom-combined-icon",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [0, -41],
  });
  combinedIconCache.set(cacheKey, icon);
  return icon;
};

// The task's own pin with a pulsing copy of itself behind it, for the searched account.
const getSearchedMarkerIcon = (taskStatus, taskIconType, taskColor) => {
  const base = getCombinedMarkerIcon(taskStatus, taskIconType, taskColor);
  return L.divIcon({
    ...base.options,
    className: `${base.options.className || ""} searched-account-pin`,
    html: `
      <div style="position: relative;">
        <div class="animate-ping" style="position: absolute; inset: 0; opacity: 0.75; transform-origin: 50% 100%;">${base.options.html}</div>
        <div style="position: relative;">${base.options.html}</div>
      </div>
    `,
  });
};

// Helper: distance in km between two [lat, lng] points (for display)
const getDistanceKm = (startLat, startLng, endLat, endLng) => {
  try {
    const from = turf.point([Number(startLng), Number(startLat)]);
    const to = turf.point([Number(endLng), Number(endLat)]);
    return turf.distance(from, to, { units: "kilometers" });
  } catch {
    return null;
  }
};

// Helper: get field agent name from tracking item (assignees or assigneeName, etc.)
const getFieldAgentName = (item) => {
  if (item.assignees && Array.isArray(item.assignees) && item.assignees.length > 0) {
    const a = item.assignees[0];
    return a.name || a.userName || a.firstName || a.assigneeName || "—";
  }
  if (item.assigneeName) return item.assigneeName;
  if (item.fieldAgentName) return item.fieldAgentName;
  return "—";
};

// Helper: format task status for display
const formatTaskStatus = (status) => {
  if (!status) return "—";
  if (status === "In_Progress") return "In Progress";
  if (status === "On_Hold") return "On Hold";
  return status;
};

// Helper: format priority for display
const formatPriority = (priority) => {
  if (!priority) return "—";
  return String(priority).replace(/_/g, " ");
};

const getTaskLatLng = (task) => {
  const lat = Number(task?.latitude);
  const lng = Number(task?.longitude);

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    return null;
  }

  return [lat, lng];
};

// Colors for multiple tracking lines (so each record is visually distinct)
const TRACKING_LINE_COLORS = ["#236DB4", "#E53935", "#43A047", "#FB8C00", "#8E24AA", "#00ACC1"];

const formatTrackingDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "—";
  }
};

// Renders tracking polylines using exact API coordinates: startLatitude, startLongitude, endLatitude, endLongitude (sorted by startCreatedDate)
const TrackingPolylines = ({ trackingData }) => {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    const startedAt = perfNow();
    if (!map) return;
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }
    if (!trackingData?.length) return;

    const group = L.layerGroup();
    const total = trackingData.length;

    // Sort by startCreatedDate so segments appear in chronological order
    const sorted = [...trackingData].sort((a, b) => {
      const t1 = a.startCreatedDate ? new Date(a.startCreatedDate).getTime() : 0;
      const t2 = b.startCreatedDate ? new Date(b.startCreatedDate).getTime() : 0;
      return t1 - t2;
    });
    const tol = 1e-6;
    const bucketSize = tol;
    const toNum = (v) => Number(v);
    const toBucket = (v) => Math.floor(v / bucketSize);
    const toKey = (lat, lng) => `${toBucket(lat)}|${toBucket(lng)}`;
    const startBuckets = new Map();
    const endBuckets = new Map();

    const addToBuckets = (bucketMap, lat, lng, item) => {
      const key = toKey(lat, lng);
      if (!bucketMap.has(key)) bucketMap.set(key, []);
      bucketMap.get(key).push(item);
    };

    sorted.forEach((item) => {
      const sLat = toNum(item.startLatitude);
      const sLng = toNum(item.startLongitude);
      const eLat = toNum(item.endLatitude);
      const eLng = toNum(item.endLongitude);
      if (Number.isFinite(sLat) && Number.isFinite(sLng)) {
        addToBuckets(startBuckets, sLat, sLng, item);
      }
      if (Number.isFinite(eLat) && Number.isFinite(eLng)) {
        addToBuckets(endBuckets, eLat, eLng, item);
      }
    });

    const getNearbyItems = (bucketMap, lat, lng, latField, lngField) => {
      const latBucket = toBucket(lat);
      const lngBucket = toBucket(lng);
      const candidates = [];
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          const key = `${latBucket + dy}|${lngBucket + dx}`;
          const list = bucketMap.get(key);
          if (list?.length) candidates.push(...list);
        }
      }
      return candidates.filter((t) => {
        const latValue = toNum(t[latField]);
        const lngValue = toNum(t[lngField]);
        return (
          Number.isFinite(latValue) &&
          Number.isFinite(lngValue) &&
          Math.abs(latValue - lat) < tol &&
          Math.abs(lngValue - lng) < tol
        );
      });
    };

    sorted.forEach((item, index) => {
      const startLat = item.startLatitude;
      const startLng = item.startLongitude;
      const endLat = item.endLatitude;
      const endLng = item.endLongitude;
      if (
        startLat != null && startLng != null &&
        endLat != null && endLng != null &&
        !Number.isNaN(Number(startLat)) && !Number.isNaN(Number(startLng)) &&
        !Number.isNaN(Number(endLat)) && !Number.isNaN(Number(endLng))
      ) {
        const start = [Number(startLat), Number(startLng)];
        const end = [Number(endLat), Number(endLng)];

        const distanceKm = getDistanceKm(startLat, startLng, endLat, endLng);
        const distanceText = distanceKm != null
          ? (distanceKm >= 1 ? `${distanceKm.toFixed(2)} km` : `${(distanceKm * 1000).toFixed(0)} m`)
          : "—";

        const taskStatus = formatTaskStatus(item.taskStatus);
        const fieldAgentName = getFieldAgentName(item);
        const priority = formatPriority(item.priority);
        const lineColor = TRACKING_LINE_COLORS[index % TRACKING_LINE_COLORS.length];

        const startDateStr = formatTrackingDate(item.startCreatedDate);
        const endDateStr = formatTrackingDate(item.endCreatedDate);

        // Tracking line popup: Task ID, Account, Status, Field Agent, Priority, Distance, Start/End dates
        const linePopupHtml = [
          "<div class='min-w-[180px]'>",
          "<strong>Task ID:</strong> " + (item.taskId ?? "—") + "<br/>",
          "<strong>Account:</strong> " + (item.accountNo || "N/A") + "<br/>",
          "<strong>Task Status:</strong> " + taskStatus + "<br/>",
          "<strong>Field Agent:</strong> " + fieldAgentName + "<br/>",
          "<strong>Priority:</strong> " + priority + "<br/>",
          "<strong>Distance:</strong> " + distanceText + "<br/>",
          "<strong>Start:</strong> " + startDateStr + "<br/>",
          "<strong>End:</strong> " + endDateStr + "<br/>",
          "<span class='text-gray-500 text-xs'>Tracking line " + (index + 1) + " of " + total + "</span>",
          "</div>",
        ].join("");

        const polyline = L.polyline([start, end], {
          color: lineColor,
          weight: 4,
          opacity: 0.9,
        });
        polyline.bindPopup(linePopupHtml);
        group.addLayer(polyline);

        const tasksAtThisStart = getNearbyItems(
          startBuckets,
          Number(startLat),
          Number(startLng),
          "startLatitude",
          "startLongitude"
        );
        const tasksAtThisEnd = getNearbyItems(
          endBuckets,
          Number(endLat),
          Number(endLng),
          "endLatitude",
          "endLongitude"
        );

        const buildTasksAtPointPopupHtml = (label, tasksAtPoint) => {
          const count = tasksAtPoint.length;
          const parts = [
            "<div style='min-width:200px;max-height:280px;overflow-y:auto;'>",
            "<strong style='font-size:13px'>" + label + "</strong><br/><span style='color:#6b7280;font-size:11px'>" + count + " task(s) at this point</span><hr style='margin:6px 0;border-color:#e5e7eb'/>",
          ];
          tasksAtPoint.forEach((t, i) => {
            const tStatus = formatTaskStatus(t.taskStatus);
            const tAgent = getFieldAgentName(t);
            const tPriority = formatPriority(t.priority);
            const tDist = getDistanceKm(t.startLatitude, t.startLongitude, t.endLatitude, t.endLongitude);
            const tDistText = tDist != null ? (tDist >= 1 ? tDist.toFixed(2) + " km" : (tDist * 1000).toFixed(0) + " m") : "—";
            const tStartDate = formatTrackingDate(t.startCreatedDate);
            const tEndDate = formatTrackingDate(t.endCreatedDate);
            parts.push(
              "<div style='padding:6px 0;border-bottom:1px solid #f3f4f6'>",
              "<strong>Task " + (i + 1) + ":</strong><br/>",
              "<strong>Task ID:</strong> " + (t.taskId ?? "—") + "<br/>",
              "<strong>Account:</strong> " + (t.accountNo || "N/A") + "<br/>",
              "<strong>Status:</strong> " + tStatus + "<br/>",
              "<strong>Field Agent:</strong> " + tAgent + "<br/>",
              "<strong>Priority:</strong> " + tPriority + "<br/>",
              "<strong>Distance:</strong> " + tDistText + "<br/>",
              "<strong>Start:</strong> " + tStartDate + "<br/>",
              "<strong>End:</strong> " + tEndDate + "<br/>",
              "</div>"
            );
          });
          parts.push("</div>");
          return parts.join("");
        };

        const startIcon = L.divIcon({
          className: "tracking-point-marker tracking-start",
          html: "<span class='tracking-point-label'>S</span>",
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        });
        const startMarker = L.marker(start, { icon: startIcon });
        startMarker.bindPopup(buildTasksAtPointPopupHtml("Start point", tasksAtThisStart));
        group.addLayer(startMarker);

        const endIcon = L.divIcon({
          className: "tracking-point-marker tracking-end",
          html: "<span class='tracking-point-label'>E</span>",
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        });
        const endMarker = L.marker(end, { icon: endIcon });
        endMarker.bindPopup(buildTasksAtPointPopupHtml("End point", tasksAtThisEnd));
        group.addLayer(endMarker);
      }
    });
    group.addTo(map);
    layerRef.current = group;
    perfLog("TrackingPolylines rendered", {
      records: trackingData.length,
      durationMs: Number((perfNow() - startedAt).toFixed(2)),
    });
    return () => {
      if (layerRef.current && map.hasLayer(layerRef.current)) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, trackingData]);

  return null;
};

const getTaskPopupHtml = (task) => `
            <div>
              <strong>Task ID:</strong> ${task.taskId}<br/>
              <strong>Account No:</strong> ${task.accountNo || 'N/A'}<br/>
              <strong>Status:</strong> ${task.taskStatus || 'N/A'}
            </div>
          `;

const SEARCHED_PIN_ZOOM = 17;

// Tracking button is hidden for now; set to true to bring it back.
const SHOW_TRACKING_BUTTON = false;

// Header Calendar opens the in-map date bar (as in UAT); set to true for the floating header calendar panel.
const USE_HEADER_CALENDAR_PANEL = false;

const MarkerClusterComponent = ({ markers, onMarkerClick, highlightTaskIds, focusHighlight = true }) => {
  const map = useMap();
  const markerClusterGroupRef = useRef(null);
  const onMarkerClickRef = useRef(onMarkerClick);
  const markerByTaskIdRef = useRef(new Map());
  const highlightIdsRef = useRef([]);
  const focusHighlightRef = useRef(focusHighlight);
  focusHighlightRef.current = focusHighlight;
  const highlightedRef = useRef([]);
  const pendingFocusRef = useRef(false);
  const revealTokenRef = useRef(0);

  // Swap the searched tasks' own pins to the animated icon, restoring any previously searched pins.
  const applyHighlightIcons = () => {
    highlightedRef.current.forEach(({ marker, task }) => {
      marker.setIcon(getCombinedMarkerIcon(task.taskStatus, task.icon, task.color));
      marker.setZIndexOffset(0);
    });
    const found = (highlightIdsRef.current || [])
      .map((id) => markerByTaskIdRef.current.get(String(id)))
      .filter(Boolean);
    found.forEach(({ marker, task }) => {
      marker.setIcon(getSearchedMarkerIcon(task.taskStatus, task.icon, task.color));
      marker.setZIndexOffset(1000);
    });
    highlightedRef.current = found;
    return found;
  };

  // Opens the cluster holding `marker` once the map has settled. A resize mid-flight (the detail
  // panel opening) fires an early moveend and can cut the flight short, so re-fly once if needed.
  const revealWhenSettled = (marker, targetZoom, refly) => {
    const token = ++revealTokenRef.current;
    let reflown = false;
    const onMoveEnd = () => {
      if (token !== revealTokenRef.current) return;
      if (refly && !reflown && map.getZoom() < targetZoom - 1) {
        reflown = true;
        map.once("moveend", onMoveEnd);
        refly();
        return;
      }
      const group = markerClusterGroupRef.current;
      if (group && group.hasLayer(marker) && group.getVisibleParent(marker) !== marker) {
        group.zoomToShowLayer(marker);
      }
    };
    map.once("moveend", onMoveEnd);
  };

  // Zoom to the searched pins, then open their cluster if they are still grouped.
  const focusHighlighted = (found) => {
    if (!found.length || !markerClusterGroupRef.current) return;
    const bounds = L.latLngBounds(found.map(({ marker }) => marker.getLatLng()));
    const single = bounds.getNorthEast().equals(bounds.getSouthWest());
    const targetZoom = single
      ? Math.max(map.getZoom(), SEARCHED_PIN_ZOOM)
      : Math.min(map.getBoundsZoom(bounds, false, L.point(120, 120)), SEARCHED_PIN_ZOOM);
    const fly = () => {
      if (single) map.flyTo(bounds.getCenter(), targetZoom);
      else map.flyToBounds(bounds, { padding: [60, 60], maxZoom: SEARCHED_PIN_ZOOM });
    };
    revealWhenSettled(found[0].marker, targetZoom, fly);
    fly();
  };

  useEffect(() => {
    onMarkerClickRef.current = onMarkerClick;
  }, [onMarkerClick]);

  useEffect(() => {
    const startedAt = perfNow();
    let renderedMarkers = 0;
    let rafId = null;
    let cancelled = false;

    // Remove existing marker cluster group if it exists
    if (markerClusterGroupRef.current) {
      map.removeLayer(markerClusterGroupRef.current);
      markerClusterGroupRef.current = null;
    }

    // Create a new marker cluster group
    const markerClusterGroup = L.markerClusterGroup();
    const tasks = Array.isArray(markers) ? markers : [];
    const markerByTaskId = new Map();
    markerByTaskIdRef.current = markerByTaskId;
    highlightedRef.current = [];
    const BATCH_SIZE = 400;
    let index = 0;

    const renderBatch = () => {
      if (cancelled) return;
      const end = Math.min(index + BATCH_SIZE, tasks.length);
      for (; index < end; index += 1) {
        const task = tasks[index];
        const latLng = getTaskLatLng(task);
        if (!latLng) continue;
        const marker = L.marker(latLng, {
          icon: getCombinedMarkerIcon(task.taskStatus, task.icon, task.color),
        });

        if (task.taskId) {
          marker.bindPopup(getTaskPopupHtml(task));
          markerByTaskId.set(String(task.taskId), { marker, task });
        }

        if (onMarkerClickRef.current) {
          marker.on("click", () => {
            onMarkerClickRef.current(task);
          });
        }

        markerClusterGroup?.addLayer(marker);
        renderedMarkers += 1;
      }

      if (index < tasks.length) {
        rafId = window.requestAnimationFrame(renderBatch);
        return;
      }
      map?.addLayer(markerClusterGroup);
      markerClusterGroupRef.current = markerClusterGroup;
      const found = applyHighlightIcons();
      if (pendingFocusRef.current && found.length) {
        pendingFocusRef.current = false;
        focusHighlighted(found);
      }
      perfLog("MarkerCluster rendered", {
        inputMarkers: tasks.length,
        renderedMarkers,
        durationMs: Number((perfNow() - startedAt).toFixed(2)),
      });
    };

    if (tasks.length > 0) {
      renderBatch();
    } else {
      perfLog("MarkerCluster rendered", {
        inputMarkers: 0,
        renderedMarkers: 0,
        durationMs: Number((perfNow() - startedAt).toFixed(2)),
      });
    }

    return () => {
      cancelled = true;
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
      // Cleanup: remove the marker cluster group when component unmounts or markers change
      if (markerClusterGroupRef.current && map.hasLayer(markerClusterGroupRef.current)) {
        map.removeLayer(markerClusterGroupRef.current);
        markerClusterGroupRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, markers]);

  useEffect(() => {
    highlightIdsRef.current = highlightTaskIds || [];
    revealTokenRef.current += 1;
    const found = applyHighlightIcons();
    if (found.length) {
      pendingFocusRef.current = false;
      if (focusHighlightRef.current) focusHighlighted(found);
      // Not every pin is on the map, so the search zooms itself; still open the cluster afterwards.
      else revealWhenSettled(found[0].marker, -Infinity, null);
    } else {
      // Pins may still be rendering; focus once they are on the map. Pins hidden by filters are not awaited.
      const ids = new Set(highlightIdsRef.current.map(String));
      pendingFocusRef.current = focusHighlightRef.current && (Array.isArray(markers) ? markers : []).some(
        (m) => ids.has(String(m.taskId)) && getTaskLatLng(m)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightTaskIds]);

  return null;
};

// Pins for searched tasks that are not loaded on the map (hidden by filters or over the map's task limit).
const SearchedTaskMarkers = ({ tasks, onClick }) => {
  const map = useMap();
  const onClickRef = useRef(onClick);

  useEffect(() => {
    onClickRef.current = onClick;
  }, [onClick]);

  useEffect(() => {
    const layers = (tasks || []).map((task) => {
      const marker = L.marker(getTaskLatLng(task), {
        icon: getSearchedMarkerIcon(task.taskStatus, task.icon, task.color),
        zIndexOffset: 1000,
      });
      marker.bindPopup(getTaskPopupHtml(task));
      marker.on("click", () => onClickRef.current?.(task));
      marker.addTo(map);
      return marker;
    });
    return () => layers.forEach((marker) => map.removeLayer(marker));
  }, [map, tasks]);

  return null;
};

const buildTaskStatusCountsFromApi = (value) => {
  if (!value) return {};
  return {
    "Active": value.activeCount ?? 0,
    "In Progress": value.inProgressCount ?? 0,
    "Completed": value.completedCount ?? 0,
    "Assigned": value.assignedCount ?? 0,
    "On_Hold": value.onHoldCount ?? 0,
  };
};

const displayStatusToApiStatus = (displayStatus) => {
  const map = {
    "Active": "Active",
    "In Progress": "In_Progress",
    "Completed": "Completed",
    "Assigned": "Assigned",
    "On_Hold": "On_Hold",
  };
  return map[displayStatus] ?? displayStatus;
};

const apiStatusToDisplayStatus = (apiStatus) => {
  const map = {
    Active: "Active",
    In_Progress: "In Progress",
    Completed: "Completed",
    Assigned: "Assigned",
    On_Hold: "On_Hold",
  };
  return map[apiStatus] ?? apiStatus;
};

// Same fixed order/colors DetailPanel used to render Task Status - moved here
// alongside the pill row that replaced it.
const TASK_STATUS_PILL_ORDER = ["Active", "In Progress", "Completed", "Assigned", "On_Hold"];

const getTaskStatusDotColor = (status) => {
  switch (status) {
    case "Active":
      return "#4379F2";
    case "In Progress":
    case "In_Progress":
      return "#FFA500";
    case "Assigned":
      return "#F3C623";
    case "Completed":
      return "#6EC207";
    case "On_Hold":
      return "#FF3333";
    default:
      return "#A0A0A0";
  }
};

const getTaskStatusPillLabel = (langMode, statusKey) => {
  const tr = translations[langMode];
  switch (statusKey) {
    case "Active":
      return tr?.statusActiveLabel ?? statusKey;
    case "In Progress":
      return tr?.statusInProgressLabel ?? statusKey;
    case "Completed":
      return tr?.statusCompletedLabel ?? statusKey;
    case "Assigned":
      return tr?.statusAssignedLabel ?? statusKey;
    case "On_Hold":
      return tr?.statusOnHoldLabel ?? statusKey;
    default:
      return statusKey;
  }
};

// Build selectedLocation from markers at (lat, lng) so detail panel stays in sync after assign/reassign
const buildSelectedLocationFromMarkers = (markers, latitude, longitude) => {
  if (!markers?.length || latitude == null || longitude == null) return null;
  const tolerance = 0.0001;
  const selectedLat = Number(latitude);
  const selectedLng = Number(longitude);
  const locationTasks = markers.filter((task) => {
    const latLng = getTaskLatLng(task);
    if (!latLng) return false;
    const [lat, lng] = latLng;
    const latDiff = Math.abs(lat - selectedLat);
    const lngDiff = Math.abs(lng - selectedLng);
    return latDiff < tolerance && lngDiff < tolerance;
  });
  if (locationTasks.length === 0) return null;
  const taskStatusCounts = {};
  ["Active", "In Progress", "Completed", "Assigned", "On_Hold"].forEach((s) => (taskStatusCounts[s] = 0));
  locationTasks.forEach((task) => {
    const status = task.taskStatus || "Unknown";
    let displayStatus = status;
    if (status === "In_Progress") displayStatus = "In Progress";
    else if (status === "On_Hold") displayStatus = "On_Hold";
    if (taskStatusCounts.hasOwnProperty(displayStatus)) {
      taskStatusCounts[displayStatus] = (taskStatusCounts[displayStatus] || 0) + 1;
    }
  });
  let agentName = null;
  let agentId = null;
  const firstTask = locationTasks[0];
  if (firstTask.assignees && firstTask.assignees.length > 0) {
    const assignee = firstTask.assignees[0];
    agentName = assignee.name || assignee.userName || assignee.firstName || null;
    agentId = assignee.userId || assignee.id || null;
  }
  const tasksWithDetails = locationTasks.map((task) => {
    const taskName = task.accountNo ? `Task ${task.accountNo}` : `Task ${task.taskId}`;
    return {
      ...task,
      taskName,
      scheduledTime: task.createdDate || task.scheduledDate,
      assignee: agentName,
    };
  });
  return {
    latitude,
    longitude,
    agent: agentName || "Unassigned",
    agentId,
    tasks: tasksWithDetails,
    taskStatusCounts,
  };
};

const DrawControl = ({ markers, drawingEnabled, drawnItems, drawingTool, onShapeDrawn, isRtl = false, isMobileViewport = false }) => {
  const map = useMap();
  const activeDrawerRef = useRef(null);
  const markerCoordinates = useMemo(
    () =>
      (Array.isArray(markers) ? markers : [])
        .map((marker) => ({ marker, latLng: getTaskLatLng(marker) }))
        .filter((entry) => Boolean(entry.latLng)),
    [markers]
  );

  // add drawnItems once
  useEffect(() => {
    if (!map) return;
    map.addLayer(drawnItems);
    return () => map.removeLayer(drawnItems);
  }, [map, drawnItems]);

  // ---------- CUSTOM DRAW UI ----------
  useEffect(() => {
    if (!map || !drawingEnabled) return;

    const control = L.control({ position: isRtl ? "topleft" : "topright" });
    let resizeObserver = null;
    let toolbarMutationObserver = null;
    let positionTimerIds = [];
    let followRafId = null;

    const syncDrawControlPosition = () => {
      const container = control.getContainer?.();
      const mapEl = document.getElementById("map");
      if (!container || !mapEl) return;

      const toolbarEl = mapEl.querySelector(".map-view-toolbar");
      const drawButtonEl = toolbarEl?.querySelector('[data-map-toolbar-button-id="edit"]');

      if (!toolbarEl || !drawButtonEl) {
        container.style.opacity = "0";
        container.style.pointerEvents = "none";
        return;
      }

      const mapRect = mapEl.getBoundingClientRect();
      const drawRect = drawButtonEl.getBoundingClientRect();
      const toolbarRect = toolbarEl.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const gap = 8;
      const verticalPadding = 8;
      const horizontalPadding = 8;

      container.style.position = "fixed";
      container.style.zIndex = "2200";
      container.style.marginTop = "0px";
      container.style.marginRight = "0px";
      container.style.marginLeft = "0px";
      container.style.opacity = "1";
      container.style.pointerEvents = "auto";

      const minTop = mapRect.top + verticalPadding;
      const maxTop = mapRect.bottom - containerRect.height - verticalPadding;
      const minLeft = mapRect.left + horizontalPadding;
      const maxLeft = mapRect.right - containerRect.width - horizontalPadding;

      // On mobile (or horizontal toolbar layout), place the flyout directly ABOVE the toolbar
      const isHorizontalToolbar = isMobileViewport || toolbarRect.width > toolbarRect.height * 1.5;

      if (isHorizontalToolbar) {
        const top = Math.max(minTop, Math.min(maxTop, toolbarRect.top - containerRect.height - gap));
        const centeredLeft = drawRect.left + (drawRect.width - containerRect.width) / 2;
        const left = Math.max(minLeft, Math.min(maxLeft, centeredLeft));
        container.style.top = `${Math.round(top)}px`;
        container.style.left = `${Math.round(left)}px`;
        container.style.right = "";
        return;
      }

      // Desktop: place to the side of the vertical toolbar
      const centeredTop = drawRect.top + (drawRect.height - containerRect.height) / 2;
      const top = Math.max(minTop, Math.min(maxTop, centeredTop));
      container.style.top = `${Math.round(top)}px`;

      const preferredLeftLtr = toolbarRect.left - containerRect.width - gap;
      const alternateLeftLtr = toolbarRect.right + gap;
      const preferredLeftRtl = toolbarRect.right + gap;
      const alternateLeftRtl = toolbarRect.left - containerRect.width - gap;
      const clampLeft = (value) => Math.max(minLeft, Math.min(maxLeft, value));
      const canFit = (value) => value >= minLeft && value <= maxLeft;
      let left;

      if (isRtl) {
        left = canFit(preferredLeftRtl)
          ? preferredLeftRtl
          : canFit(alternateLeftRtl)
            ? alternateLeftRtl
            : clampLeft(preferredLeftRtl);
      } else {
        left = canFit(preferredLeftLtr)
          ? preferredLeftLtr
          : canFit(alternateLeftLtr)
            ? alternateLeftLtr
            : clampLeft(preferredLeftLtr);
      }
      container.style.left = `${Math.round(left)}px`;
      container.style.right = "";
    };

    const followFor = (durationMs = 420) => {
      if (typeof performance === "undefined") {
        syncDrawControlPosition();
        return;
      }
      const startedAt = performance.now();
      const step = (now) => {
        syncDrawControlPosition();
        if (now - startedAt < durationMs) {
          followRafId = window.requestAnimationFrame(step);
        } else {
          followRafId = null;
        }
      };
      if (followRafId) {
        window.cancelAnimationFrame(followRafId);
      }
      followRafId = window.requestAnimationFrame(step);
    };

    control.onAdd = () => {
      const container = L.DomUtil.create("div", "custom-draw custom-draw-flyout");

      const rectBtn = L.DomUtil.create("button", "custom-btn", container);
      const circleBtn = L.DomUtil.create("button", "custom-btn", container);
      const lineBtn = L.DomUtil.create("button", "custom-btn", container);

      const activateDraw = (type, btn) => {
        if (drawnItems) {
          drawnItems.clearLayers();
        }

        // disable previous draw
        if (activeDrawerRef.current) {
          activeDrawerRef.current.disable();
          activeDrawerRef.current = null;
        }

        container
          .querySelectorAll("button")
          .forEach((b) => b.classList.remove("active"));

        let drawer;
        if (type === "rectangle") drawer = new L.Draw.Rectangle(map);
        if (type === "circle") drawer = new L.Draw.Circle(map);
        if (type === "polyline")
          drawer = new L.Draw.Polyline(map, {
            shapeOptions: { color: "#1E88E5" },
            freehand: true,
          });

        drawer.enable();
        activeDrawerRef.current = drawer;
        btn.classList.add("active");
      };

      rectBtn.onclick = () => activateDraw("rectangle", rectBtn);
      circleBtn.onclick = () => activateDraw("circle", circleBtn);
      lineBtn.onclick = () => activateDraw("polyline", lineBtn);

      createRoot(rectBtn).render(<Square size={16} />);
      createRoot(circleBtn).render(<Circle size={18} />);
      createRoot(lineBtn).render(<Ruler size={18} />);

      L.DomEvent.disableClickPropagation(container);
      return container;
    };

    control.addTo(map);
    syncDrawControlPosition();

    positionTimerIds = [
      window.setTimeout(syncDrawControlPosition, 0),
      window.setTimeout(syncDrawControlPosition, 120),
      window.setTimeout(syncDrawControlPosition, 280),
    ];

    const onResize = () => followFor(520);
    const onScroll = () => syncDrawControlPosition();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);

    const mapEl = document.getElementById("map");
    const toolbarEl = mapEl?.querySelector(".map-view-toolbar");
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        followFor(300);
      });
      if (mapEl) resizeObserver.observe(mapEl);
      if (toolbarEl) resizeObserver.observe(toolbarEl);
    }
    if (toolbarEl && typeof MutationObserver !== "undefined") {
      toolbarMutationObserver = new MutationObserver(() => {
        followFor(520);
      });
      toolbarMutationObserver.observe(toolbarEl, {
        attributes: true,
        attributeFilter: ["style", "class"],
      });
      toolbarMutationObserver.observe(toolbarEl, {
        childList: true,
        subtree: true,
      });
      toolbarEl.addEventListener("transitionstart", onResize);
      toolbarEl.addEventListener("transitionend", syncDrawControlPosition);
    }
    map.on("move zoom resize", syncDrawControlPosition);

    return () => {
      control.remove();
      positionTimerIds.forEach((id) => window.clearTimeout(id));
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
      map.off("move zoom resize", syncDrawControlPosition);
      if (resizeObserver) resizeObserver.disconnect();
      if (toolbarMutationObserver) toolbarMutationObserver.disconnect();
      if (toolbarEl) {
        toolbarEl.removeEventListener("transitionstart", onResize);
        toolbarEl.removeEventListener("transitionend", syncDrawControlPosition);
      }
      if (followRafId) {
        window.cancelAnimationFrame(followRafId);
      }
    };
  }, [map, drawingEnabled, drawnItems, isRtl, isMobileViewport]);

  // ---------- DRAW CREATED ----------
  useEffect(() => {
    if (!map || !drawingEnabled) return;

    const onDrawCreated = (event) => {
      const drawStartedAt = perfNow();
      const { layer, layerType } = event;

      // stop draw mode
      if (activeDrawerRef.current) {
        activeDrawerRef.current.disable();
        activeDrawerRef.current = null;
      }

      let filteredMarkers = [];

      // ===================== POLYLINE ? FREEHAND LASSO (PENCIL) =====================
      if (layerType === "polyline") {
        const latLngs = layer.getLatLngs();
        const points = latLngs.map((p) => [p.lng, p.lat]);

        const line = turf.lineString(points);

        const smooth = turf.bezierSpline(line, {
          resolution: 10000,
          sharpness: 0.85,
        });

        const coords = smooth.geometry.coordinates;
        coords.push(coords[0]);

        let polygon = turf.polygon([coords]);

        // REMOVE INNER STRAIGHT LINES
        polygon = turf.buffer(polygon, 0, { units: "meters" });

        const polygonLayer = L.geoJSON(polygon, {
          style: {
            color: "#1E88E5",
            weight: 4,
            fillOpacity: 0.25,
          },
        });
        drawnItems.addLayer(polygonLayer);

        // Find markers inside the drawn polygon
        filteredMarkers = markerCoordinates.filter(({ latLng }) => {
          const [lat, lng] = latLng;
          const pt = turf.point([lng, lat]);
          return turf.booleanPointInPolygon(pt, polygon);
        }).map(({ marker }) => marker);
      }

      // ===================== RECTANGLE =====================
      else if (layerType === "rectangle") {
        drawnItems.addLayer(layer);
        const bounds = layer.getBounds();

        filteredMarkers = markerCoordinates.filter(({ latLng }) => {
          return latLng ? bounds.contains(latLng) : false;
        }).map(({ marker }) => marker);
      }

      // ===================== CIRCLE =====================
      else if (layerType === "circle") {
        drawnItems.addLayer(layer);
        const center = layer.getLatLng();
        const radius = layer.getRadius();

        filteredMarkers = markerCoordinates.filter(({ latLng }) => {
          if (!latLng) return false;
          const distance = map.distance(latLng, center);
          return distance <= radius;
        }).map(({ marker }) => marker);
      }

      if (typeof onShapeDrawn === "function") {
        onShapeDrawn(filteredMarkers);
      }
      perfLog("Draw selection completed", {
        layerType,
        totalMarkers: markerCoordinates.length,
        selectedMarkers: Array.isArray(filteredMarkers) ? filteredMarkers.length : 0,
        durationMs: Number((perfNow() - drawStartedAt).toFixed(2)),
      });
    };

    map.on(L.Draw.Event.CREATED, onDrawCreated);

    return () => {
      map.off(L.Draw.Event.CREATED, onDrawCreated);
    };
  }, [map, drawingEnabled, markerCoordinates, drawnItems, drawingTool, onShapeDrawn]);

  return null;
}

// Normalize the group list from the get-by-department API into a shape the modals render.
// API group: { id, groupName, departmentId, userIds: [], members: [{ userId, userName }] }
const normalizeGroups = (rawGroups) =>
  (Array.isArray(rawGroups) ? rawGroups : []).map((g) => ({
    groupId: g?.id ?? g?.groupId ?? g?.groupByDepartmentId,
    groupName: g?.groupName || "-",
    members: (Array.isArray(g?.members) ? g.members : []).map((m) => ({
      id: m?.userId ,
      name: m?.userName || "Unknown",
    })),
  }));

// Does a group match the search query (by group name or any member name)?
const groupMatchesQuery = (group, query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  return (
    (group.groupName || "").toLowerCase().includes(q) ||
    group.members.some((m) => (m.name || "").toLowerCase().includes(q))
  );
};

const filterGroupsByAssignedMembers = (groups, assignedUserIds) => {
  const assignedSet = new Set(
    (Array.isArray(assignedUserIds) ? assignedUserIds : [])
      .filter((id) => id !== undefined && id !== null && id !== "")
      .map((id) => String(id))
  );
  if (!assignedSet.size) return [];
  return (Array.isArray(groups) ? groups : []).reduce((acc, group) => {
    const members = (Array.isArray(group?.members) ? group.members : []).filter((m) =>
      assignedSet.has(String(m?.id))
    );
    if (members.length) acc.push({ ...group, members });
    return acc;
  }, []);
};

// Users / Groups tab switcher shared by the assign, reassign and unassign modals.
// Hidden entirely when the logged-in user has no group access, leaving only the
// (default) "users" scope available to them.
const AssignScopeTabs = ({ scope, onScopeChange, langMode }) => {
  if (!currentUserCanUseGroups()) return null;
  return (
  <div className="mb-4 flex w-full rounded-lg border border-gray-200 bg-gray-50 p-1">
    {[
      { key: "users", label: translations[langMode]?.userAssign ?? "User Assign" },
      { key: "groups", label: translations[langMode]?.groupAssign ?? "Group Assign" },
    ].map((tab) => (
      <button
        key={tab.key}
        type="button"
        onClick={() => onScopeChange(tab.key)}
        className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition ${
          scope === tab.key
            ? "bg-white text-blue-700 shadow-sm"
            : "text-gray-500 hover:text-gray-700"
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
  );
};

const GroupSelectList = ({ groups, selectedGroupId, onSelectGroup, query, langMode, allowedMemberIds }) => {
  const [expanded, setExpanded] = useState({});

  if (!groups.length) {
    return (
      <p className="text-sm text-gray-500 py-4 text-center">
        {translations[langMode]?.nogroupfound ?? "No group found"}
      </p>
    );
  }

  const q = (query || "").trim().toLowerCase();
  const toggleExpand = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const allowedSet = allowedMemberIds
    ? new Set((Array.isArray(allowedMemberIds) ? allowedMemberIds : Array.from(allowedMemberIds)).map((id) => String(id)))
    : null;
  const isMemberAllowed = (m) => !allowedSet || allowedSet.has(String(m?.id));
  const noAccessText =
    translations[langMode]?.memberNoWorkflowAccess ?? "No access to this workflow";

  const groupRows = groups.map((group) => {
    const isSelected = selectedGroupId != null && group.groupId === selectedGroupId;
    const hasMembers = group.members.length > 0;
    const nameMatches = q ? (group.groupName || "").toLowerCase().includes(q) : false;
    const matchingMembers = q
      ? group.members.filter((m) => (m.name || "").toLowerCase().includes(q))
      : [];
    // Auto-expand a group when the search matched one of its members (unless the user toggled it).
    // Groups without members have no accordion, so they never open.
    const isOpen =
      hasMembers &&
      (expanded[group.groupId] ?? (matchingMembers.length > 0 && !nameMatches));
    const membersToShow = q && matchingMembers.length > 0 ? matchingMembers : group.members;
    
    const allowedMemberCount = group.members.filter((m) => isMemberAllowed(m)).length;
    const isSelectable = allowedMemberCount > 0;
    const toggleSelect = () => {
      if (!isSelectable) return;
      onSelectGroup(isSelected ? null : group.groupId);
    };

    return (
      <div key={group.groupId} className="rounded-lg">
        <div
          className={`flex items-center gap-3 py-2 px-2 rounded-lg ${
            isSelected ? "bg-blue-50" : isSelectable ? "hover:bg-gray-50" : ""
          }`}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={toggleSelect}
            disabled={!isSelectable}
            className={`w-4 h-4 text-blue-600 rounded ${isSelectable ? "" : "cursor-not-allowed opacity-40"}`}
          />
          <button
            type="button"
            onClick={toggleSelect}
            disabled={!isSelectable}
            className={`flex flex-1 items-center gap-3 min-w-0 text-left ${isSelectable ? "" : "cursor-not-allowed opacity-60"}`}
          >
            <span className="h-8 w-8 shrink-0 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-semibold">
              {String(group.groupName).charAt(0)}
            </span>
            <span className="min-w-0 truncate text-sm font-medium text-gray-800">
              {group.groupName}
            </span>
          </button>
          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
            {group.members.length}
          </span>
          <button
            type="button"
            onClick={() => hasMembers && toggleExpand(group.groupId)}
            disabled={!hasMembers}
            aria-label={isOpen ? "Collapse members" : "Expand members"}
            className={`shrink-0 p-1 text-gray-400 ${
              hasMembers
                ? "hover:text-gray-600"
                : "opacity-40 cursor-not-allowed"
            }`}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
        </div>
        {isOpen && (
          <div className="ml-11 mr-2 mb-2 border-l border-gray-200 pl-3">
            {membersToShow.length > 0 ? (
              membersToShow.map((m) => {
                const allowed = isMemberAllowed(m);
                return (
                  <div
                    key={m.id}
                    className={`flex items-center gap-2 py-1 ${allowed ? "" : "opacity-50"}`}
                  >
                    <span
                      className={`h-6 w-6 shrink-0 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                        allowed ? "bg-gray-200 text-gray-600" : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {String(m.name).charAt(0)}
                    </span>
                    <span
                      className={`min-w-0 truncate text-xs ${
                        allowed ? "text-gray-700" : "text-gray-600"
                      }`}
                    >
                      {m.name}
                    </span>
                    {!allowed && (
                      <span className="shrink-0 text-[10px] italic text-gray-600">
                        {noAccessText}
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="py-1 text-xs text-gray-400">
                {translations[langMode]?.nomembers ?? "No members"}
              </p>
            )}
          </div>
        )}
      </div>
    );
  });

  return (
    <>
      {allowedSet && (
        <p className="mb-2 px-1 text-xs text-gray-500">
          {translations[langMode]?.assignWorkflowAccessNote ??
            "Only group members who have access to this workflow will be assigned the task."}
        </p>
      )}
      {groupRows}
    </>
  );
};

// Assign modal: select agent(s) and assign to active task(s) at selected location
const AssignAgentModal = ({
  title,
  agents,
  groups: groupsProp,
  selectedAgentIds,
  onToggleAgent,
  selectedGroupId,
  onSelectGroup,
  onConfirm,
  onClose,
  loading,
  langMode,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredAgents = useMemo(() => {
    const list = Array.isArray(agents) ? agents : [];
    const query = (searchQuery || "").trim().toLowerCase();
    if (!query) return list;
    return list.filter((agent) => {
      const name = (agent?.name || agent?.userName || agent?.firstName || "").toLowerCase();
      return name.includes(query);
    });
  }, [agents, searchQuery]);

  const filteredAgentIds = useMemo(
    () =>
      filteredAgents
        .map((agent) => agent?.userId || agent?.id)
        .filter((id) => id !== undefined && id !== null && id !== ""),
    [filteredAgents]
  );

  const [scope, setScope] = useState("users");
  const groups = useMemo(() => normalizeGroups(groupsProp), [groupsProp]);
  const filteredGroups = useMemo(() => {
    if (!(searchQuery || "").trim()) return groups;
    return groups.filter((g) => groupMatchesQuery(g, searchQuery));
  }, [groups, searchQuery]);
  // Members allowed for the group tab = field agents with access to this workflow.
  const workflowAgentIds = useMemo(
    () =>
      (Array.isArray(agents) ? agents : [])
        .map((agent) => agent?.userId || agent?.id)
        .filter((id) => id !== undefined && id !== null && id !== ""),
    [agents]
  );

  const allFilteredSelected =
    filteredAgentIds.length > 0 &&
    filteredAgentIds.every((id) => selectedAgentIds.includes(id));

  const handleToggleAllFiltered = () => {
    if (!filteredAgentIds.length) return;
    const shouldSelectAll = !allFilteredSelected;
    const selectedSet = new Set(selectedAgentIds);

    filteredAgentIds.forEach((id) => {
      const isSelected = selectedSet.has(id);
      if ((shouldSelectAll && !isSelected) || (!shouldSelectAll && isSelected)) {
        onToggleAgent(id);
      }
    });
  };

  const canConfirm = scope === "groups" ? selectedGroupId != null : selectedAgentIds.length > 0;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[10000] p-3 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90dvh] overflow-hidden flex flex-col">
        <div className="p-4 sm:p-6 overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-bold text-gray-800">{title}</h3>
          <ModalCloseButton onClick={onClose} ariaLabel="Close assign modal" />
        </div>
        <p className="text-gray-600 mb-4">
          {scope === "groups"
            ? (translations[langMode]?.selectgrouptoassign ?? "Select group(s) to assign.")
            : (translations[langMode]?.selectassigntounassign ?? "Select agent(s) to assign.")}
        </p>
        <AssignScopeTabs scope={scope} onScopeChange={setScope} langMode={langMode} />
        <div className="mb-3 flex items-center gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={
              scope === "groups"
                ? (translations[langMode]?.searchGroup ?? "Search Groups")
                : (translations[langMode]?.searchfieldAgent ?? "Search Field Agents")
            }
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          />
          {scope === "users" && (
            <CommonButton colorIntent="primary"
              type="button"
              variant="outline"
              onClick={handleToggleAllFiltered}
              disabled={filteredAgentIds.length === 0}
              tooltipText={
                allFilteredSelected
                  ? (translations[langMode]?.deselectAllAgentsTooltip ?? "Deselect all visible agents")
                  : (translations[langMode]?.selectAllAgentsTooltip ?? "Select all visible agents")
              }
              tooltipPosition="top"
              className="!h-10 !min-h-[40px] !px-3 !text-xs !whitespace-nowrap"
            >
              {allFilteredSelected
                ? (translations[langMode]?.deselectAll ?? "Deselect All")
                : (translations[langMode]?.selectAll ?? "Select All")}
            </CommonButton>
          )}
        </div>
        <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-2 mb-6">
          {scope === "groups" ? (
            <GroupSelectList
              groups={filteredGroups}
              selectedGroupId={selectedGroupId}
              onSelectGroup={onSelectGroup}
              query={searchQuery}
              langMode={langMode}
              allowedMemberIds={workflowAgentIds}
            />
          ) : filteredAgents.length > 0 ? (
            filteredAgents.map((agent) => {
              const id = agent.userId || agent.id;
              const name = agent.name || agent.userName || agent.firstName || "Unknown";
              return (
                <label
                  key={id}
                  className="flex items-center gap-3 cursor-pointer py-2 px-2 hover:bg-gray-50 rounded-lg"
                >
                  <input
                    type="checkbox"
                    checked={selectedAgentIds.includes(id)}
                    onChange={() => onToggleAgent(id)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="h-8 w-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-semibold">
                    {name.charAt(0)}
                  </span>
                  <span className="text-sm font-medium text-gray-800">{name}</span>
                </label>
              );
            })
          ) : (
            <p className="text-sm text-gray-500 py-4 text-center">
              {translations[langMode]?.nofieldagent ?? "No agents available"}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-3">
          <CommonButton colorIntent="danger" onClick={onClose} variant="danger" tooltipPosition="top">
            {translations[langMode]?.cancel ?? "Cancel"}
          </CommonButton>
          <CommonButton colorIntent="success"
            onClick={onConfirm}
            variant="primary"
            disabled={!canConfirm || loading}
            tooltipPosition="top"
          >
            {loading ? (translations[langMode]?.Processing ?? "Processing...") : (translations[langMode]?.assign ?? "Assign")}
          </CommonButton>
        </div>
        </div>
      </div>
    </div>
  );
};

// Unassign modal: select assignee(s) to unassign from task(s)
const UnassignConfirmationModal = ({
  assignees,
  groups: groupsProp,
  onClose,
  onConfirm,
  selectedAssignees,
  onToggleAssignee,
  onToggleAllAssignees,
  selectedGroupId,
  onSelectGroup,
  langMode,

}) => {
  const [searchQuery, setSearchQuery] = useState("");
  // Only show assignees who belong to a group assigned to this user
  // (blank group list => unrestricted => show all assignees).
  const allowedMemberIds = useMemo(() => {
    if (!getCurrentUserGroupIds().length) return null;
    return getGroupMemberIdSet(groupsProp);
  }, [groupsProp]);
  const list = useMemo(() => {
    const base = Array.isArray(assignees) ? assignees : [];
    if (!allowedMemberIds) return base;
    return base.filter((a) =>
      allowedMemberIds.has(String(a?.assigneeId ?? a?.userId ?? a?.id ?? ""))
    );
  }, [assignees, allowedMemberIds]);

  const filteredAssignees = useMemo(() => {
    const query = (searchQuery || "").trim().toLowerCase();
    if (!query) return list;
    return list.filter((assignee) => {
      const name = String(assignee?.name || assignee?.userName || assignee?.assigneeName || "").toLowerCase();
      return name.includes(query);
    });
  }, [list, searchQuery]);

  const getAssigneeId = (assignee) => assignee?.userId || assignee?.assigneeId || assignee?.id;

  const filteredIds = useMemo(
    () =>
      filteredAssignees
        .map((assignee) => getAssigneeId(assignee))
        .filter((id) => id !== undefined && id !== null && id !== ""),
    [filteredAssignees]
  );

  const [scope, setScope] = useState("users");
  const groups = useMemo(() => normalizeGroups(groupsProp), [groupsProp]);
  // Only show groups that contain a currently-assigned user, mirroring the user list.
  const assignedGroups = useMemo(
    () =>
      filterGroupsByAssignedMembers(
        groups,
        list.map((assignee) => getAssigneeId(assignee))
      ),
    [groups, list]
  );
  const filteredGroups = useMemo(() => {
    if (!(searchQuery || "").trim()) return assignedGroups;
    return assignedGroups.filter((g) => groupMatchesQuery(g, searchQuery));
  }, [assignedGroups, searchQuery]);

  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedAssignees.includes(id));

  const handleToggleAllFiltered = () => {
    if (!filteredIds.length) return;
    const shouldSelectAll = !allFilteredSelected;
    const selectedSet = new Set(selectedAssignees);

    filteredIds.forEach((id) => {
      const isSelected = selectedSet.has(id);
      if ((shouldSelectAll && !isSelected) || (!shouldSelectAll && isSelected)) {
        onToggleAssignee(id);
      }
    });
  };

  const canConfirm = scope === "groups" ? selectedGroupId != null : selectedAssignees.length > 0;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[10000] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-6">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-bold text-gray-800">
            {translations[langMode]?.unassigntask ?? "Unassign Task"}
          </h3>
          <ModalCloseButton onClick={onClose} ariaLabel="Close unassign modal" />
        </div>
        <p className="text-gray-600 mb-6">
          {scope === "groups"
            ? (translations[langMode]?.selectgrouptounassign ?? "Select group(s) to unassign.")
            : (translations[langMode]?.selectassigntounassign ?? "Select assignee(s) to unassign.")}
        </p>
        <AssignScopeTabs scope={scope} onScopeChange={setScope} langMode={langMode} />
        <div className="mb-3 flex items-center gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={
              scope === "groups"
                ? (translations[langMode]?.searchGroup ?? "Search Groups")
                : (translations[langMode]?.searchfieldAgent ?? "Search Field Agents")
            }
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          />
          {scope === "users" && (
            <CommonButton colorIntent="primary"
              type="button"
              variant="outline"
              onClick={handleToggleAllFiltered}
              disabled={filteredIds.length === 0}
              tooltipText={
                allFilteredSelected
                  ? (translations[langMode]?.deselectAllAgentsTooltip ?? "Deselect all visible assignees")
                  : (translations[langMode]?.selectAllAgentsTooltip ?? "Select all visible assignees")
              }
              tooltipPosition="top"
              className="!h-10 !min-h-[40px] !px-3 !text-xs !whitespace-nowrap"
            >
              {allFilteredSelected
                ? (translations[langMode]?.deselectAll ?? "Deselect All")
                : (translations[langMode]?.selectAll ?? "Select All")}
            </CommonButton>
          )}
        </div>
        <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
          <div className="max-h-60 overflow-y-auto pr-2">
            {scope === "groups" ? (
              <GroupSelectList
                groups={filteredGroups}
                selectedGroupId={selectedGroupId}
                onSelectGroup={onSelectGroup}
                query={searchQuery}
                langMode={langMode}
              />
            ) : filteredAssignees.length > 0 ? (
              filteredAssignees.map((assignee) => (
                <label
                  key={getAssigneeId(assignee)}
                  className="flex items-center space-x-3 text-gray-700 cursor-pointer py-2 hover:bg-gray-100 rounded-md transition px-2"
                >
                  <input
                    type="checkbox"
                    className="form-checkbox h-5 w-5 text-indigo-600 rounded"
                    checked={selectedAssignees.includes(getAssigneeId(assignee))}
                    onChange={() => onToggleAssignee(getAssigneeId(assignee))}
                  />
                  <div className="flex-1 flex items-center">
                    <span className="h-8 w-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-semibold mr-3">
                      {(assignee.name || assignee.userName || assignee.assigneeName || "?").charAt(0)}
                    </span>
                    <span className="text-base">{assignee.name || assignee.userName || assignee.assigneeName || "Unknown"}</span>
                  </div>
                </label>
              ))
            ) : (
              <p className="text-sm text-gray-500 py-4 text-center">
                {translations[langMode]?.nofieldagent ?? "No assignees available"}
              </p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <CommonButton colorIntent="danger" onClick={onClose} variant="danger" tooltipPosition="top">
            {translations[langMode]?.cancel ?? "Cancel"}
          </CommonButton>
          <CommonButton colorIntent="warning"
            onClick={onConfirm}
            variant="primary"
            disabled={!canConfirm}
            tooltipPosition="top"
          >
            {translations[langMode]?.unassignn ?? "Unassign"}
            {scope === "users" ? ` (${selectedAssignees.length})` : ""}
          </CommonButton>
        </div>
      </div>
    </div>
  );
};

const MapView = () => {
  const [markers, setMarkers] = useState([]);
  const [searchedTasks, setSearchedTasks] = useState([]);
  const [searchPinsAllOnMap, setSearchPinsAllOnMap] = useState(true);
  // Set of field-agent userIds the current user may see, derived from their accessible groups.
  // null = no group restriction (super admin, or a user with no assigned groups) => show all tasks.
  const [groupAllowedAgentIds, setGroupAllowedAgentIds] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);
  const [isTaskDetailLoading, setIsTaskDetailLoading] = useState(false);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [showBottomPanel, setShowBottomPanel] = useState(false);
  const [bottomPanelExpanded, setBottomPanelExpanded] = useState(false);
  const [analyticsFilters, setAnalyticsFilters] = useState({
    status: "",
    fieldAgentId: "",
    fromDate: null,
    toDate: null,
  });
  const [taskStatusCounts, setTaskStatusCounts] = useState({});
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);
  // Customized marker colors (from CustomMarkerSettingsModal), keyed by
  // taskStatus - used so the status filter dots match whatever color the
  // user picked for that status, falling back to the same defaults
  // getCombinedMarkerIcon already uses when nothing's been customized.
  const [customStatusColors, setCustomStatusColors] = useState({});
  const [mapType, setMapType] = useState("map"); // map | satellite
  const navigate = useNavigate();
  const [drawingEnabled, setDrawingEnabled] = useState(false);
  const [drawnItems] = useState(new L.FeatureGroup());
  const [drawingTool, setDrawingTool] = useState("arc");

  // Modals for Assign / Reassign / Unassign / Delete (marker-selected tasks)
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCustomMarkerModal, setShowCustomMarkerModal] = useState(false);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  // Group (entity) selection for the "Group assign" tab — sent to the backend as groupId.
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedUnassignGroupId, setSelectedUnassignGroupId] = useState(null);
  const [assigneesToUnassign, setAssigneesToUnassign] = useState([]);
  const [selectedAssigneesToUnassign, setSelectedAssigneesToUnassign] = useState([]);
  const [deleteComment, setDeleteComment] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  // modals for download
  const [downloadModal, setDownloadModal] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
  const [priority, setPriority] = useState("");
  const [status, setStatus] = useState("");
  const [acc, setAcc] = useState("");

  // Calendar & Date Panel Logic
  const MAX_PAST_DAYS = 90;
  const WINDOW_SIZE = 7;
  const today = new Date();
  const [showDatePanel, setShowDatePanel] = useState(false);
  const datePanelRef = useRef(null);
  const [datePanelHeight, setDatePanelHeight] = useState(0);
  const datePanelOffset = showDatePanel ? datePanelHeight : 0;
  const [selectedDates, setSelectedDates] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [activeRange, setActiveRange] = useState(null);
  const [windowStartDate, setWindowStartDate] = useState(new Date());
  const [range, setRange] = useState([null, null]);
  const [startDate, endDate] = range;

  // Tracking: optional field agent + date filters
  const [isTrackingEnabled, setIsTrackingEnabled] = useState(false); // Controls if tracking lines are shown on map
  const [showTrackingPanel, setShowTrackingPanel] = useState(false); // Controls filter modal visibility only
  const [headerActivitySlotEl, setHeaderActivitySlotEl] = useState(null);
  const [headerExportSlotEl, setHeaderExportSlotEl] = useState(null);
  const [headerCalendarTriggerSlotEl, setHeaderCalendarTriggerSlotEl] = useState(null);
  const [headerCalendarPanelSlotEl, setHeaderCalendarPanelSlotEl] = useState(null);
  const [headerCalendarPanelStyle, setHeaderCalendarPanelStyle] = useState(null);
  // Whether the desktop-header Export button should show icon-only (see the
  // overlap-measuring effect further down, near where exportHeaderButtonControl
  // is defined) - declared up here since exportHeaderButtonControl's JSX
  // below reads it and hooks can't be referenced before initialization.
  const [isExportCompact, setIsExportCompact] = useState(false);
  const [trackingData, setTrackingData] = useState([]);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingFieldAgentId, setTrackingFieldAgentId] = useState("");
  const [trackingDate, setTrackingDate] = useState("");
  const [trackingFieldAgentList, setTrackingFieldAgentList] = useState([]);
  const minDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() - MAX_PAST_DAYS);
    return d;
  })();

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [activityAnalyticsDate, setActivityAnalyticsDate] = useState({
    fromDate : null, toDate : null
  });
  const lastTaskListRequestKeyRef = useRef("");
  const lastAnalyticsStatusChangeAtRef = useRef(0);
  const latestMapDataRequestIdRef = useRef(0);
  const latestTrackingRequestIdRef = useRef(0);

  const context = useContext(AppContext);
  const {
    workflowId,
    userType,
    setSelectedOrgId,
    selectedOrgId,
    taskStatusContext,
    setTaskStatusContext,
    langMode,
    theme,
    activityId,
    updateActivityId,
    activityLoader,
    active,
    projectId,
  } = context;
  const isRtl = langMode === "ar" || langMode === "ur";
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 639px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mediaQuery = window.matchMedia("(max-width: 639px)");
    const updateViewportMode = () => setIsMobileViewport(mediaQuery.matches);
    updateViewportMode();
    mediaQuery.addEventListener?.("change", updateViewportMode);

    return () => {
      mediaQuery.removeEventListener?.("change", updateViewportMode);
    };
  }, []);

  // Separate from isMobileViewport above (which stays at its own 639px
  // threshold for all other mobile layout tweaks). This one matches
  // MainNav's own header-collapse breakpoint (Tailwind `lg`, set to 1214px
  // in tailwind.config.js) so Activity/Calendar/Export portal into the
  // hamburger drawer's slots instead of the desktop header row's slots
  // whenever MainNav itself is showing the drawer.
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 1213px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mediaQuery = window.matchMedia("(max-width: 1213px)");
    const updateHeaderMode = () => setIsHeaderCollapsed(mediaQuery.matches);
    updateHeaderMode();
    mediaQuery.addEventListener?.("change", updateHeaderMode);

    return () => {
      mediaQuery.removeEventListener?.("change", updateHeaderMode);
    };
  }, []);

  // Activity dropdown (moved from Bottom Panel to top-right near list view button)
  const [activityList, setActivityList] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  useEffect(() => {
    const hasMapModalOpen =
      showAssignModal ||
      showReassignModal ||
      showUnassignModal ||
      showDeleteModal ||
      showCustomMarkerModal;

    document.body.classList.toggle("modal-open", hasMapModalOpen);

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [showAssignModal, showReassignModal, showUnassignModal, showDeleteModal, showCustomMarkerModal]);

  useEffect(() => {
    const fetchActivityList = async () => {
      if (!projectId) {
        setActivityList([]);
        return;
      }
      try {
        const data = await getActivityListByProjectId(projectId);
        setActivityList(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching activities:", error);
        setActivityList([]);
      }
    };
    fetchActivityList();
  }, [projectId]);
  
  // Activities the logged-in user is permitted to see (from localStorage userDetails.workflowList).
  const permittedActivityList = useMemo(() => {
    return getPermittedActivities(activityList);
  }, [activityList]);

  const activityOptions = useMemo(
    () =>
      (permittedActivityList || [])
        .filter(Boolean)
        .map((item) => ({ value: item.workflowId, label: item.workflowName })),
    [permittedActivityList]
  );
  const defaultActivityValue = useMemo(() => {
    if (projectId == null || !activityId || !permittedActivityList?.length) return null;
    const activityIdList =
      typeof activityId === "string"
        ? activityId.split(",").map((s) => s.trim())
        : Array.isArray(activityId)
          ? activityId.map((id) => id.toString())
          : [activityId?.toString()];
    const matched = (permittedActivityList || []).filter((org) =>
      activityIdList.includes(String(org.workflowId))
    );
    if (matched.length === 0) return null;
    const first = matched[0];
    return { value: first.workflowId, label: first.workflowName };
  }, [projectId, activityId, permittedActivityList]);
  useEffect(() => {
    if (defaultActivityValue && !selectedActivity) {
      setSelectedActivity(defaultActivityValue);
    }
  }, [defaultActivityValue, selectedActivity]);
  const activitySelectValue = selectedActivity ?? defaultActivityValue ?? null;
  const handleChangeActivity = useCallback(
    (selectedValue) => {
      if (!selectedValue) return;
      updateActivityId(selectedValue.value);
      setSelectedActivity(selectedValue);
      localStorage.setItem("workflowName", selectedValue.label);
    },
    [updateActivityId]
  );
  const PlaceholderComponent = (props) => <components.Placeholder {...props} />;
  const calendarTriggerLabel = useMemo(() => {
    if (fromDate && toDate) {
      return `${format(fromDate)} - ${format(toDate)}`;
    }
    return translations[langMode]?.calendarLabel ?? "Calendar";
  }, [fromDate, toDate, langMode]);

  const activitySelectorControl = !activityLoader ? (
    <div className="w-full">
      <ReactSelector
        options={projectId != null ? activityOptions : []}
        onChange={handleChangeActivity}
        value={activitySelectValue}
        components={{ Placeholder: PlaceholderComponent }}
        placeholder={
          projectId != null && activityOptions.length > 0
            ? translations[langMode].select
            : translations[langMode].noactivity
        }
        isClearable={false}
        isSearchable={true}
        height="42px"
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      />
    </div>
  ) : null;

  const calendarHeaderTriggerControl = (
    <div className="w-full">
      <button
        type="button"
        onClick={() => handleToolbarAction("calendar")}
        className={`flex h-[42px] w-full items-center justify-between gap-2 rounded-[10px] border bg-white px-3 text-sm font-medium transition-colors ${
          showDatePanel ? "border-[#236DB4] text-[#236DB4]" : "border-gray-200 text-slate-700 hover:border-gray-300"
        }`}
        aria-label={translations[langMode]?.calendarLabel ?? "Calendar"}
      >
        <span className="inline-flex items-center gap-2 min-w-0">
          <Calendar size={16} />
          <span className="truncate">{calendarTriggerLabel}</span>
        </span>
        <ChevronDown size={16} className={`transition-transform ${showDatePanel ? "rotate-180" : ""}`} />
      </button>
    </div>
  );
  const exportHeaderButtonControl = (
    <Tooltip text={translations[langMode]?.mapTipExport ?? "Export Task Report"} position="bottom">
      <button
        type="button"
        onClick={() => handleToolbarAction("download")}
        className="inline-flex h-[42px] items-center gap-2 rounded-[10px] border border-gray-200 bg-white px-5 text-[15px] font-medium text-slate-700 shadow-sm transition-colors hover:border-gray-300"
        aria-label="Export"
      >
        <Download size={18} />
        <span className={isHeaderCollapsed ? "inline" : isExportCompact ? "hidden" : "hidden lg:inline"}>
          Export
        </span>
      </button>
    </Tooltip>
  );

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const syncHeaderSlot = () => {
      const activitySlotId = isHeaderCollapsed ? "map-activity-drawer-slot" : "map-activity-header-slot";
      const exportSlotId = isHeaderCollapsed ? "map-export-drawer-slot" : "map-export-header-slot";
      const calendarTriggerSlotId = isHeaderCollapsed
        ? "map-calendar-drawer-trigger-slot"
        : "map-calendar-header-trigger-slot";
      setHeaderActivitySlotEl(document.getElementById(activitySlotId));
      setHeaderExportSlotEl(document.getElementById(exportSlotId));
      setHeaderCalendarTriggerSlotEl(document.getElementById(calendarTriggerSlotId));
      // The floating header-anchored calendar panel only makes sense next to
      // the desktop header trigger - in the drawer, the trigger just toggles
      // showDatePanel and the existing inline map date panel takes over.
      setHeaderCalendarPanelSlotEl(
        isHeaderCollapsed ? null : document.getElementById("map-calendar-header-panel-slot")
      );
    };

    syncHeaderSlot();
    const timer = window.setTimeout(syncHeaderSlot, 0);
    return () => window.clearTimeout(timer);
  }, [isHeaderCollapsed]);

  // Desktop header row only: on narrower desktop widths (e.g. 14" laptops)
  // the fixed-width Department/Project/Activity/Calendar group can leave too
  // little room before Export, so Export's own label starts visually
  // colliding with the Calendar button next to it. Rather than guessing a
  // fixed pixel breakpoint (laptop resolutions/DPI scaling vary too much),
  // measure the actual live gap between the two and drop Export to icon-only
  // once they get too close - same as icon-only already looks at every
  // narrower breakpoint. Hysteresis (tighter threshold to go compact than to
  // expand back) keeps this from flickering back and forth right at the edge.
  useEffect(() => {
    if (isHeaderCollapsed || typeof document === "undefined") {
      setIsExportCompact(false);
      return undefined;
    }

    const checkOverlap = () => {
      const exportEl = document.getElementById("map-export-header-slot");
      const calendarEl = document.getElementById("map-calendar-header-trigger-slot");
      if (!exportEl || !calendarEl) return;

      const gap = exportEl.getBoundingClientRect().left - calendarEl.getBoundingClientRect().right;
      setIsExportCompact((prevCompact) => (prevCompact ? gap < 100 : gap < 8));
    };

    checkOverlap();
    const timeoutIds = [
      window.setTimeout(checkOverlap, 0),
      window.setTimeout(checkOverlap, 180),
      window.setTimeout(checkOverlap, 360),
    ];
    window.addEventListener("resize", checkOverlap);

    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      window.removeEventListener("resize", checkOverlap);
    };
  }, [isHeaderCollapsed, headerExportSlotEl, headerCalendarTriggerSlotEl]);

  const shouldRenderCalendarInHeader = USE_HEADER_CALENDAR_PANEL && !isHeaderCollapsed && Boolean(headerCalendarPanelSlotEl);
  const showDatePanelInMap = showDatePanel && !shouldRenderCalendarInHeader;

  const updateHeaderCalendarPanelPosition = useCallback(() => {
    if (
      typeof window === "undefined" ||
      !showDatePanel ||
      !shouldRenderCalendarInHeader ||
      !headerCalendarTriggerSlotEl
    ) {
      setHeaderCalendarPanelStyle(null);
      return;
    }

    const rect = headerCalendarTriggerSlotEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const desiredWidth = 880;
    const maxWidth = Math.max(320, viewportWidth - 32);
    const width = Math.min(desiredWidth, maxWidth);

    let left = rect.left;
    const rightLimit = viewportWidth - 16;
    if (left + width > rightLimit) {
      left = rightLimit - width;
    }
    left = Math.max(16, left);

    setHeaderCalendarPanelStyle({
      position: "fixed",
      top: Math.round(rect.bottom + 8),
      left: Math.round(left),
      width: Math.round(width),
      zIndex: 2200,
    });
  }, [showDatePanel, shouldRenderCalendarInHeader, headerCalendarTriggerSlotEl]);

  useEffect(() => {
    if (!showDatePanel || !shouldRenderCalendarInHeader || !headerCalendarTriggerSlotEl) {
      setHeaderCalendarPanelStyle(null);
      return;
    }

    updateHeaderCalendarPanelPosition();
    const onRelayout = () => updateHeaderCalendarPanelPosition();
    window.addEventListener("resize", onRelayout);
    window.addEventListener("scroll", onRelayout, true);
    return () => {
      window.removeEventListener("resize", onRelayout);
      window.removeEventListener("scroll", onRelayout, true);
    };
  }, [showDatePanel, shouldRenderCalendarInHeader, headerCalendarTriggerSlotEl, updateHeaderCalendarPanelPosition]);

  const getVisibleDates = () => {
    const dates = [];

    // Ensure windowStartDate does not create future dates
    const maxStartDate = new Date(today);
    maxStartDate.setDate(today.getDate() - (WINDOW_SIZE - 1));
    const startDate =
      windowStartDate > maxStartDate ? maxStartDate : windowStartDate;

    for (let i = 0; i < WINDOW_SIZE; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      dates.push(d);
    }

    return dates;
  };

  function isSelected(date) {
    if (!fromDate || !toDate) return false;

    return (
      isSameDay(date, fromDate) ||
      isSameDay(date, toDate) ||
      (isAfter(date, fromDate) && isBefore(date, toDate))
    );

  }

  //single date click ? 1-day range
  function toggleDate(date) {
    if (
      fromDate &&
      toDate &&
      isSameDay(fromDate, date) &&
      isSameDay(toDate, date)
    ) {
      setFromDate(null);
      setToDate(null);
      setActiveRange(null);
      return;
    }

    // Otherwise ? SELECT single day
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);

    const end = new Date(date);
    end.setHours(23, 59, 59, 999);

    setFromDate(start);
    setToDate(end);
    setActiveRange(null);
  }

  function format(date) {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
    });
  }

  function moveDate(offset) {
    setWindowStartDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() + offset);

      const min = new Date();
      min.setDate(today.getDate() - MAX_PAST_DAYS);

      const maxStart = new Date(today);
      maxStart.setDate(today.getDate() - (WINDOW_SIZE - 1));

      if (newDate < min) return min;
      if (newDate > maxStart) return maxStart;

      return newDate;
    });
  }

  function toggleLastDays(days) {

    const end = new Date();
    end.setHours(23, 59, 59, 999);

    const start = new Date();
    start.setDate(start.getDate() - (days - 1));
    start.setHours(0, 0, 0, 0);

    // Toggle OFF if already active
    if (activeRange === days) {
      setFromDate(null);
      setToDate(null);
      setActiveRange(null);
      return;
    }

    setFromDate(start);
    setToDate(end);
    setActiveRange(days);

    // keep date window aligned
    setWindowStartDate(start);
  }

  const handleChange = (dates) => {
    const [from, to] = dates;
    setRange(dates);

    if (!from || !to) return;

    // Create array of dates between from ? to
    const allDates = [];
    const current = new Date(from);

    while (current <= to) {
      allDates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    setSelectedDates(allDates);
    setActiveRange(null);
    setWindowStartDate(from);
  };

  const formatDateYYYYMMDD = (date) => {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const handleAnalyticsStatusChange = useCallback((statusValue = "") => {
    lastAnalyticsStatusChangeAtRef.current = Date.now();
    const nextStatus = statusValue || "";
    setAnalyticsFilters((prev) => ({ ...prev, status: nextStatus }));
    setTaskStatusContext(nextStatus);
    setSelectedStatusFilter(nextStatus ? apiStatusToDisplayStatus(nextStatus) : null);
  }, [setTaskStatusContext]);

  const handleAnalyticsFieldAgentChange = useCallback((fieldAgentId = "") => {
    const nextFieldAgentId = fieldAgentId || "";
    setAnalyticsFilters((prev) => ({ ...prev, fieldAgentId: nextFieldAgentId }));
    setSelectedAssigneeId(nextFieldAgentId);
  }, []);

  const handleAnalyticsDateChange = useCallback((nextDates) => {
    const nextFromDate = nextDates?.fromDate ?? null;
    const nextToDate = nextDates?.toDate ?? null;
    setActivityAnalyticsDate({ fromDate: nextFromDate, toDate: nextToDate });
    setAnalyticsFilters((prev) => ({
      ...prev,
      fromDate: nextFromDate,
      toDate: nextToDate,
    }));
  }, []);

  const fetchTasks = useCallback(async () => {
    const requestId = ++latestMapDataRequestIdRef.current;
    const startedAt = perfNow();
    setIsLoading(true);
    if (activityId == null) {
      if (requestId !== latestMapDataRequestIdRef.current) return;
      setIsLoading(false);
      setMarkers([]);
      setTaskStatusCounts({});
      return;
    }
    // While Activity Analytics is open, the map uses the same shared filters as that panel.
    const effectiveStatusFilter = showBottomPanel ? analyticsFilters.status : (taskStatusContext || "");
    const dynamicTaskStatus = effectiveStatusFilter || undefined;
    const effectiveAssigneeId = showBottomPanel ? analyticsFilters.fieldAgentId : selectedAssigneeId;
    console.log("Fetching tasks with:", {
      activityId,
      taskStatus: dynamicTaskStatus,
      assigneeId: effectiveAssigneeId,
    });

    const formattedFromDate = formatDateYYYYMMDD(fromDate);
    const formattedToDate = formatDateYYYYMMDD(toDate);
    const activityAnalyticsDateFormattedFromDate = formatDateYYYYMMDD(analyticsFilters.fromDate);
    const activityAnalyticsDateFormattedToDate = formatDateYYYYMMDD(analyticsFilters.toDate);
    const fromDates = showBottomPanel ? activityAnalyticsDateFormattedFromDate : formattedFromDate;
    const toDates = showBottomPanel ? activityAnalyticsDateFormattedToDate : formattedToDate;
    const requestKey = JSON.stringify({
      activityId: String(activityId),
      taskStatus: dynamicTaskStatus ?? "",
      assigneeId: effectiveAssigneeId ?? "",
      fromDate: fromDates ?? "",
      toDate: toDates ?? "",
    });
    const isRecentAnalyticsStatusChange = Date.now() - lastAnalyticsStatusChangeAtRef.current < 500;

    // Ignore the immediate duplicate map refresh caused by analytics-only status changes.
    if (isRecentAnalyticsStatusChange && lastTaskListRequestKeyRef.current === requestKey) {
      lastAnalyticsStatusChangeAtRef.current = 0;
      if (requestId !== latestMapDataRequestIdRef.current) return;
      setIsLoading(false);
      return;
    }

    lastTaskListRequestKeyRef.current = requestKey;
    try {
      const data = await getTaskListAPI(
        activityId,
        dynamicTaskStatus,
        effectiveAssigneeId,
        fromDates,
        toDates
      );
      if (requestId !== latestMapDataRequestIdRef.current) return;

      const value = data?.body?.value;
      if (value?.taskList && Array.isArray(value.taskList)) {
        setMarkers(value.taskList);
        setTaskStatusCounts(buildTaskStatusCountsFromApi(value));
        perfLog("fetchTasks success", {
          requestId,
          taskCount: value.taskList.length,
          durationMs: Number((perfNow() - startedAt).toFixed(2)),
          status: dynamicTaskStatus || "all",
        });
      } else {
        setMarkers([]);
        setTaskStatusCounts({});
        // Harshit Vachhani - 2026-07-15: Surface backend error messages
        // (e.g. code 601 - selection has too many tasks to load on the map)
        // so the user is told why nothing rendered instead of a blank map.
        const backendMessage = data?.body?.error ?? data?.header?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        }
        perfLog("fetchTasks empty", {
          requestId,
          durationMs: Number((perfNow() - startedAt).toFixed(2)),
        });
      }
    } catch (error) {
      if (requestId !== latestMapDataRequestIdRef.current) return;
      console.error("Error fetching tasks:", error);
      setMarkers([]);
      setTaskStatusCounts({});
      perfLog("fetchTasks failed", {
        requestId,
        durationMs: Number((perfNow() - startedAt).toFixed(2)),
      });
    }
    if (requestId !== latestMapDataRequestIdRef.current) return;
    setIsLoading(false);
  }, [activityId, selectedAssigneeId, taskStatusContext, analyticsFilters, showBottomPanel, fromDate, toDate]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks, taskStatusContext, selectedAssigneeId]);

  // Fetch field agents for tracking dropdown when activity changes
  useEffect(() => {
    const load = async () => {
      if (!activityId) {
        setTrackingFieldAgentList([]);
        return;
      }
      try {
        const response = await fetchFieldAgentMap(activityId);
        const list = Array.isArray(response?.body?.value) ? response.body.value : Array.isArray(response) ? response : [];
        // Only show field agents who belong to a group assigned to this user
        // (blank group list => show all).
        setTrackingFieldAgentList(await filterAgentsByCurrentUserGroups(list));
      } catch (e) {
        console.error("Error fetching field agents for tracking:", e);
        setTrackingFieldAgentList([]);
      }
    };
    load();
  }, [activityId]);

  // Initial tracking API call when workflow is selected (only based on workflow id)
  const workflowIdForTracking = useMemo(() => {
    if (activityId == null) return null;
    const w = Array.isArray(activityId) ? activityId[0] : typeof activityId === "string" ? activityId.split(",")[0]?.trim() : activityId;
    return w != null && w !== "" ? w : null;
  }, [activityId]);

  // Call tracking API as soon as tracking is enabled (do not wait for Apply)
  useEffect(() => {
    if (!isTrackingEnabled || !workflowIdForTracking) {
      if (!isTrackingEnabled) {
        setTrackingData([]);
      }
      return;
    }
    const requestId = ++latestTrackingRequestIdRef.current;
    const startedAt = perfNow();
    let cancelled = false;
    setTrackingLoading(true);
    fetchTracking(
      workflowIdForTracking,
      trackingFieldAgentId?.trim() || "",
      trackingDate?.trim() || ""
    )
      .then((data) => {
        if (cancelled || requestId !== latestTrackingRequestIdRef.current) return;
        const value = data?.body?.value;
        if (Array.isArray(value)) setTrackingData(value);
        else setTrackingData([]);
        perfLog("tracking auto-load completed", {
          requestId,
          records: Array.isArray(value) ? value.length : 0,
          durationMs: Number((perfNow() - startedAt).toFixed(2)),
        });
      })
      .catch(() => {
        if (!cancelled && requestId === latestTrackingRequestIdRef.current) setTrackingData([]);
      })
      .finally(() => {
        if (!cancelled && requestId === latestTrackingRequestIdRef.current) setTrackingLoading(false);
      });
    return () => { cancelled = true; };
  }, [isTrackingEnabled, workflowIdForTracking, trackingFieldAgentId, trackingDate]);

  const loadTracking = useCallback(async () => {
    if (activityId == null) {
      toast.warn(translations[langMode]?.selectWorkflowFirst ?? "Select activity first.");
      return;
    }
    const requestId = ++latestTrackingRequestIdRef.current;
    const startedAt = perfNow();
    setTrackingLoading(true);
    try {
      const data = await fetchTracking(
        activityId,
        trackingFieldAgentId?.trim() || "",
        trackingDate?.trim() || ""
      );
      if (requestId !== latestTrackingRequestIdRef.current) return;
      const value = data?.body?.value;
      if (Array.isArray(value)) {
        setTrackingData(value);
        toast.success(translations[langMode]?.success ?? "Tracking loaded.");
        perfLog("tracking manual-load completed", {
          requestId,
          records: value.length,
          durationMs: Number((perfNow() - startedAt).toFixed(2)),
        });
      } else {
        setTrackingData([]);
        toast.info(translations[langMode]?.norecordfound ?? "No tracking records found.");
        perfLog("tracking manual-load empty", {
          requestId,
          durationMs: Number((perfNow() - startedAt).toFixed(2)),
        });
      }
    } catch (error) {
      if (requestId !== latestTrackingRequestIdRef.current) return;
      console.error("Error loading tracking:", error);
      setTrackingData([]);
      const backendMessage = getBackendErrorMessage(error);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      if (requestId === latestTrackingRequestIdRef.current) {
        setTrackingLoading(false);
      }
    }
  }, [activityId, trackingFieldAgentId, trackingDate, langMode]);

  // After assign/reassign/unassign: refetch Active + Assigned so map and detail panel show updated status
  const refetchAndSyncSelection = useCallback(async () => {
    if (activityId == null) return;
    const formattedFromDate = formatDateYYYYMMDD(fromDate);
    const formattedToDate = formatDateYYYYMMDD(toDate);
    try {
      const [activeData, assignedData] = await Promise.all([
        getTaskListAPI(activityId, "Active", selectedAssigneeId, formattedFromDate, formattedToDate),
        getTaskListAPI(activityId, "Assigned", selectedAssigneeId, formattedFromDate, formattedToDate),
      ]);
      const activeList = activeData?.body?.value?.taskList && Array.isArray(activeData.body.value.taskList)
        ? activeData.body.value.taskList
        : [];
      const assignedList = assignedData?.body?.value?.taskList && Array.isArray(assignedData.body.value.taskList)
        ? assignedData.body.value.taskList
        : [];
      const byId = new Map();
      activeList.forEach((t) => byId.set(t.taskId, t));
      assignedList.forEach((t) => byId.set(t.taskId, t));
      const merged = Array.from(byId.values());
      setMarkers(merged);
      const counts = { Active: 0, "In Progress": 0, Completed: 0, Assigned: 0, On_Hold: 0 };
      merged.forEach((task) => {
        const s = task.taskStatus || "Unknown";
        const key = s === "In_Progress" ? "In Progress" : s === "On_Hold" ? "On_Hold" : s;
        if (counts.hasOwnProperty(key)) counts[key] = (counts[key] || 0) + 1;
      });
      setTaskStatusCounts(counts);
      if (selectedLocation?.latitude != null && selectedLocation?.longitude != null) {
        const updated = buildSelectedLocationFromMarkers(
          merged,
          selectedLocation.latitude,
          selectedLocation.longitude
        );
        if (updated) {
          setSelectedLocation((prev) => (prev ? { ...prev, ...updated } : null));
        }
      }
    } catch (error) {
      console.error("Error refetching after assign:", error);
      fetchTasks();
    }
  }, [activityId, selectedAssigneeId, fromDate, toDate, selectedLocation?.latitude, selectedLocation?.longitude]);

  const handleChangeStatus = useCallback(
    async (status, assigneeId = "", accountNo = "") => {
      if (activityId == null) return;
      const requestId = ++latestMapDataRequestIdRef.current;
      const startedAt = perfNow();
      try {
        const data = await getTaskListAPI(
          activityId,
          status,
          assigneeId,
          "",
          "",
          accountNo
        );
        if (requestId !== latestMapDataRequestIdRef.current) return;

        const value = data?.body?.value;
        if (value?.taskList) {
          setMarkers(value.taskList);
          setTaskStatusCounts(buildTaskStatusCountsFromApi(value));
          perfLog("handleChangeStatus success", {
            requestId,
            status: status || "all",
            taskCount: Array.isArray(value.taskList) ? value.taskList.length : 0,
            durationMs: Number((perfNow() - startedAt).toFixed(2)),
          });
        } else {
          setMarkers([]);
          setTaskStatusCounts({});
          // Harshit Vachhani - 2026-07-15: Surface backend error messages
          // (e.g. code 601 - selection has too many tasks to load on the map)
          // so the user is told why nothing rendered instead of a blank map.
          const backendMessage = data?.body?.error ?? data?.header?.message;
          if (backendMessage) {
            toast.error(backendMessage);
          }
          perfLog("handleChangeStatus empty", {
            requestId,
            status: status || "all",
            durationMs: Number((perfNow() - startedAt).toFixed(2)),
          });
        }
      } catch (error) {
        if (requestId !== latestMapDataRequestIdRef.current) return;
        console.error("Map Filter Status Error :", error);
        setMarkers([]);
        setTaskStatusCounts({});
        perfLog("handleChangeStatus failed", {
          requestId,
          status: status || "all",
          durationMs: Number((perfNow() - startedAt).toFixed(2)),
        });
      }
    },
    [activityId]
  );

  const handleStatusClick = useCallback(
    (displayStatus) => {
      const apiStatus = displayStatusToApiStatus(displayStatus);
      setTaskStatusContext(apiStatus)
      setSelectedStatusFilter(displayStatus);
      handleChangeStatus(apiStatus, selectedAssigneeId, "");
    },
    [handleChangeStatus, selectedAssigneeId]
  );

  // "All" - same reset already used by handleClosePanel, just without closing
  // the panel: clears the status filter so every task shows again.
  const handleAllStatusClick = useCallback(() => {
    setTaskStatusContext(null);
    setSelectedStatusFilter(null);
    handleChangeStatus("", selectedAssigneeId, "");
  }, [handleChangeStatus, selectedAssigneeId]);

  // Filter the map's task list by the clicked field agent (assigneeId).
  const handleAgentClick = useCallback(
    (agentId) => {
      if (agentId == null) return;
      const nextId = String(agentId);
      const newAssigneeId =
        selectedAssigneeId && String(selectedAssigneeId) === nextId ? "" : nextId;
      setSelectedAssigneeId(newAssigneeId);
      setAnalyticsFilters((prev) => ({ ...prev, fieldAgentId: newAssigneeId }));
    },
    [selectedAssigneeId]
  );

  // Restrict visible tasks to members of the groups the current admin/office user can access.
  // Super admins (and users with no assigned groups) are left unrestricted.
  useEffect(() => {
    let cancelled = false;

    const userType = getCurrentUserType();
    const userGroupIds = getCurrentUserGroupIds();
    if (userType === "super_admin" || userGroupIds.length === 0) {
      setGroupAllowedAgentIds(null);
      return undefined;
    }

    (async () => {
      try {
        const groups = await loadGroupsForDepartment(); // already filtered to the user's accessible groups
        const memberIds = getGroupMemberIdSet(groups);
        if (!cancelled) setGroupAllowedAgentIds(memberIds);
      } catch (err) {
        console.error("Failed to load group-based task access:", err);
        if (!cancelled) setGroupAllowedAgentIds(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activityId]);

  // Markers shown on the map after applying group-based access:
  // restricted users see tasks assigned to their group members plus unassigned Active tasks.
  const visibleMarkers = useMemo(() => {
    const list = Array.isArray(markers) ? markers : [];
    if (!groupAllowedAgentIds) return list;
    return list.filter((task) => isTaskVisibleForGroupAccess(task, groupAllowedAgentIds));
  }, [markers, groupAllowedAgentIds]);

  // Handle marker click - group tasks by location
  const handleMarkerClick = useCallback(async (clickedTask) => {
    const clickedLatLng = getTaskLatLng(clickedTask);
    if (!clickedTask || !clickedLatLng) return;
    const [clickedLat, clickedLng] = clickedLatLng;

    // Find all tasks at the same location (within a small tolerance)
    const tolerance = 0.0001; // Approximately 11 meters
    const locationTasks = visibleMarkers.filter((task) => {
      const latLng = getTaskLatLng(task);
      if (!latLng) return false;
      const [lat, lng] = latLng;
      const latDiff = Math.abs(lat - clickedLat);
      const lngDiff = Math.abs(lng - clickedLng);
      return latDiff < tolerance && lngDiff < tolerance;
    });

    // Calculate task status counts
    const taskStatusCounts = {};
    locationTasks.forEach((task) => {
      const status = task.taskStatus || "Unknown";
      let displayStatus = status;
      if (status === "Active") displayStatus = "Active";
      else if (status === "In_Progress") displayStatus = "In Progress";
      else if (status === "Completed") displayStatus = "Completed";
      else if (status === "Assigned") displayStatus = "Assigned";
      else if (status === "On_Hold") displayStatus = "On_Hold";

      taskStatusCounts[displayStatus] = (taskStatusCounts[displayStatus] || 0) + 1;
    });

    let agentName = null;
    let agentId = null;
    if (clickedTask.assignees && clickedTask.assignees.length > 0) {
      const assignee = clickedTask.assignees[0];
      agentName = assignee.name || assignee.userName || assignee.firstName || null;
      agentId = assignee.userId || assignee.id || null;
    }

    const tasksWithDetails = locationTasks.map((task) => {
      const taskName = task.accountNo ? `Task ${task.accountNo}` : `Task ${task.taskId}`;

      return {
        ...task,
        taskName,
        scheduledTime: task.createdDate || task.scheduledDate,
        assignee: agentName,
      };
    });

    setSelectedLocation({
      latitude: clickedLat,
      longitude: clickedLng,
      agent: agentName || "Unassigned",
      agentId: agentId,
      tasks: tasksWithDetails,
      taskStatusCounts,
    });

    if (clickedTask.taskId) {
      setIsTaskDetailLoading(true);
      try {
        const taskDetail = await fetchTaskDetails(clickedTask.taskId);
        setSelectedTaskDetail(taskDetail || null);
      } catch (error) {
        console.error("Error fetching task details:", error);
        setSelectedTaskDetail(null);
      } finally {
        setIsTaskDetailLoading(false);
      }
    } else {
      setSelectedTaskDetail(null);
    }

    setShowDetailPanel(true);
  }, [visibleMarkers]);
  const handleClosePanel = () => {
    setShowDetailPanel(false);
    setSelectedLocation(null);
    setSelectedTaskDetail(null);
    setIsTaskDetailLoading(false);
    setTaskStatusContext(null)
    setSelectedAssigneeId('')
  };

  const handleClearLocation = () => {
    setSelectedLocation(null);
    setSelectedTaskDetail(null);
    setIsTaskDetailLoading(false);
    setShowDetailPanel(false);

    // CLEAR DRAWN SHAPES
    if (drawnItems) {
      drawnItems.clearLayers();
    }
  };

  // When user draws a shape (square, circle, pencil), capture tasks inside and open detail panel
  const handleShapeDrawn = useCallback(
    (filteredMarkers, { isDrawnArea = true, isAccountSearch = false } = {}) => {
      const tasks = filteredMarkers || [];
      const taskStatusCounts = {};
      const STATUS_ORDER = ["Active", "In Progress", "Completed", "Assigned", "On_Hold"];
      STATUS_ORDER.forEach((s) => (taskStatusCounts[s] = 0));
      tasks.forEach((task) => {
        const status = task.taskStatus || "Unknown";
        let displayStatus = status;
        if (status === "In_Progress") displayStatus = "In Progress";
        else if (status === "On_Hold") displayStatus = "On_Hold";
        if (STATUS_ORDER.includes(displayStatus)) {
          taskStatusCounts[displayStatus] = (taskStatusCounts[displayStatus] || 0) + 1;
        }
      });

      let agentName = "Various";
      let agentId = null;
      if (tasks.length === 1 && tasks[0].assignees?.length > 0) {
        const assignee = tasks[0].assignees[0];
        agentName = assignee.name || assignee.userName || assignee.firstName || "Unassigned";
        agentId = assignee.userId || assignee.id || null;
      } else if (tasks.length > 0) {
        const firstWithAssignee = tasks.find((t) => t.assignees?.length > 0);
        if (firstWithAssignee?.assignees?.[0]) {
          const a = firstWithAssignee.assignees[0];
          agentName = a.name || a.userName || a.firstName || "Various";
          agentId = a.userId || a.id || null;
        }
      }
      if (tasks.length === 0) agentName = "Unassigned";

      const tasksWithDetails = tasks.map((task) => {
        const taskName = task.accountNo ? `Task ${task.accountNo}` : `Task ${task.taskId}`;
        return {
          ...task,
          taskName,
          scheduledTime: task.createdDate || task.scheduledDate,
        };
      });

      const validTaskCoordinates = tasks
        .map((task) => getTaskLatLng(task))
        .filter(Boolean);
      const centerLat =
        validTaskCoordinates.length > 0
          ? validTaskCoordinates.reduce((sum, [lat]) => sum + lat, 0) / validTaskCoordinates.length
          : null;
      const centerLng =
        validTaskCoordinates.length > 0
          ? validTaskCoordinates.reduce((sum, [, lng]) => sum + lng, 0) / validTaskCoordinates.length
          : null;

      setSelectedLocation({
        latitude: centerLat,
        longitude: centerLng,
        agent: agentName,
        agentId,
        tasks: tasksWithDetails,
        taskStatusCounts,
        isDrawnArea,
        isAccountSearch,
      });
      setShowDetailPanel(true);
    },
    []
  );

  const handleAccountSearchResult = useCallback(
    async (tasks) => {
      handleShapeDrawn(tasks, { isDrawnArea: false, isAccountSearch: true });
      const firstTaskId = tasks?.[0]?.taskId;
      if (!firstTaskId) {
        setSelectedTaskDetail(null);
        return;
      }
      setIsTaskDetailLoading(true);
      try {
        setSelectedTaskDetail((await fetchTaskDetails(firstTaskId)) || null);
      } catch (error) {
        console.error("Error fetching task details:", error);
        setSelectedTaskDetail(null);
      } finally {
        setIsTaskDetailLoading(false);
      }
    },
    [handleShapeDrawn]
  );

  // Marks the searched tasks' pins; returns whether all of them are on the map (the cluster then zooms to them).
  const handleAccountHighlight = useCallback(
    (tasks) => {
      const list = (tasks || []).filter((task) => task?.taskId != null);
      const onMap = new Set(
        visibleMarkers.filter((m) => getTaskLatLng(m)).map((m) => String(m.taskId))
      );
      const allOnMap = list.length > 0 && list.every((task) => onMap.has(String(task.taskId)));
      setSearchedTasks(list);
      setSearchPinsAllOnMap(allOnMap);
      return allOnMap;
    },
    [visibleMarkers]
  );

  const searchedTaskIds = useMemo(
    () => searchedTasks.map((task) => String(task.taskId)),
    [searchedTasks]
  );

  const hiddenSearchedTasks = useMemo(() => {
    if (!searchedTasks.length) return [];
    const onMap = new Set(
      visibleMarkers.filter((m) => m.taskId != null && getTaskLatLng(m)).map((m) => String(m.taskId))
    );
    return searchedTasks.filter((task) => !onMap.has(String(task.taskId)) && getTaskLatLng(task));
  }, [searchedTasks, visibleMarkers]);

  const handleSearchedPinClick = useCallback(() => {
    if (searchedTasks.length) handleAccountSearchResult(searchedTasks);
  }, [searchedTasks, handleAccountSearchResult]);

  // Clearing the search only closes the panel if it is still showing the search result.
  const handleAccountSearchClear = useCallback(() => {
    if (!selectedLocation?.isAccountSearch) return;
    setSelectedLocation(null);
    setSelectedTaskDetail(null);
    setShowDetailPanel(false);
  }, [selectedLocation?.isAccountSearch]);

  // Task IDs at selected location (for assign/reassign/unassign/delete)
  const selectedTaskIds = selectedLocation?.tasks?.map((t) => t.taskId).filter(Boolean) ?? [];
  const hasActiveTask = selectedLocation?.tasks?.some((t) => t.taskStatus === "Active") ?? false;
  const taskActionsPerm = getPermissionByScreenCode("TASK_MANAGEMENT");
  const canUpdateTask = Boolean(taskActionsPerm?.canUpdate);
  const canDeleteTask = Boolean(taskActionsPerm?.canDelete);
  const taskActionClass = (
    enabled,
    enabledClass = "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
  ) =>
    `inline-flex h-8 lg:h-9 min-h-[32px] lg:min-h-[36px] items-center gap-1 lg:gap-2 rounded-lg px-2 lg:px-3 py-0 text-xs lg:text-sm font-medium transition-colors ${
      enabled ? enabledClass : "cursor-not-allowed text-gray-400"
    }`;

  // Keep the Task Actions bar's edge aligned right next to the Analytics
  // toggle - measure Analytics' real rendered width instead of guessing, so
  // the gap between them always matches the small gap used elsewhere in this
  // file (e.g. MapViewToolbar's row-mode gap), regardless of label length.
  const [taskActionsBarEdgeOffset, setTaskActionsBarEdgeOffset] = useState(416);

  useEffect(() => {
    if (isMobileViewport || !showDetailPanel) return undefined;

    const detailPanelOffset = 416;
    const gap = 12;

    const updateOffset = () => {
      const mapEl = document.getElementById("map");
      const analyticsEl = mapEl?.querySelector(".map-analytics-toggle-anchor");
      if (!analyticsEl) return;
      setTaskActionsBarEdgeOffset(
        Math.round(detailPanelOffset + analyticsEl.getBoundingClientRect().width + gap)
      );
    };

    updateOffset();
    const timeoutIds = [
      window.setTimeout(updateOffset, 0),
      window.setTimeout(updateOffset, 180),
      window.setTimeout(updateOffset, 360),
    ];
    window.addEventListener("resize", updateOffset);

    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      window.removeEventListener("resize", updateOffset);
    };
  }, [isMobileViewport, showDetailPanel, showBottomPanel, bottomPanelExpanded, isRtl, langMode]);

  // Prevent the Analytics toggle from rising into whichever element actually
  // shares its side on a short map (e.g. Analytics drawer expanded): the map
  // type/zoom control, which is always the lower of the two stacked controls
  // on the content side (right in LTR, left in RTL - it docks directly below
  // the icon rail on both). Measures that element's real bottom edge and caps
  // how large Analytics' `bottom` offset is allowed to get, instead of using a
  // fixed value that assumes tall maps.
  const [analyticsMaxBottomOffset, setAnalyticsMaxBottomOffset] = useState(null);

  useEffect(() => {
    if (isMobileViewport) return undefined;

    const gap = 12;

    // Deliberately NOT measured from the obstacle's own live bottom edge:
    // that control's position is itself set asynchronously (by
    // MapTypeControl's own effect), so reading it back here can race and
    // capture a stale in-between value - which either clamps Analytics too
    // hard (forcing it down under the bottom drawer) or not hard enough
    // (letting it overlap). Rebuild the same chain MapTypeControl uses to
    // position itself (trackingBottom -> rail -> zoom control) from pieces
    // that are each stable/synchronous instead: the Tracking button's CSS
    // position and the two controls' intrinsic (content-based) heights.
    const updateClamp = () => {
      const mapEl = document.getElementById("map");
      const trackingEl = mapEl?.querySelector(".map-tracking-anchor");
      const toolbarRailEl = mapEl?.querySelector(".map-view-toolbar");
      const zoomEl = mapEl?.querySelector(".map-type-zoom-control");
      const analyticsBtn = mapEl?.querySelector(".map-analytics-toggle-btn");
      if (!mapEl || !trackingEl || !toolbarRailEl || !zoomEl || !analyticsBtn) return;

      const mapRect = mapEl.getBoundingClientRect();
      const trackingBottom = trackingEl.getBoundingClientRect().bottom - mapRect.top;
      const railHeight = toolbarRailEl.getBoundingClientRect().height;
      const zoomHeight = zoomEl.getBoundingClientRect().height;
      const obstacleBottomY = trackingBottom + gap + railHeight + gap + zoomHeight;
      const analyticsHeight = analyticsBtn.getBoundingClientRect().height;

      setAnalyticsMaxBottomOffset(
        Math.round(mapRect.height - analyticsHeight - obstacleBottomY - gap)
      );
    };

    updateClamp();
    const timeoutIds = [
      window.setTimeout(updateClamp, 0),
      window.setTimeout(updateClamp, 180),
      window.setTimeout(updateClamp, 360),
    ];
    window.addEventListener("resize", updateClamp);

    const mapEl = document.getElementById("map");
    let resizeObserver = null;
    if (mapEl && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateClamp);
      resizeObserver.observe(mapEl);
      const trackingEl = mapEl.querySelector(".map-tracking-anchor");
      if (trackingEl) resizeObserver.observe(trackingEl);
      const toolbarRailEl = mapEl.querySelector(".map-view-toolbar");
      if (toolbarRailEl) resizeObserver.observe(toolbarRailEl);
    }

    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      window.removeEventListener("resize", updateClamp);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [isMobileViewport, showBottomPanel, bottomPanelExpanded, isRtl]);

  // While the Analytics drawer is open, the vertical anti-overlap clamp above
  // can still leave the toggle sitting close beneath the control rail/zoom
  // stack on a short map. Sidestep that by also nudging it sideways, clear of
  // that column, instead of relying on vertical clearance alone - measured
  // from the zoom control's real width so it tracks if that control's size
  // ever changes. Only meant to apply while open; closed keeps its original
  // position untouched (the effect simply isn't consulted then - see the
  // style below).
  const [analyticsOpenShift, setAnalyticsOpenShift] = useState(0);

  useEffect(() => {
    if (isMobileViewport || !showBottomPanel) return undefined;

    const gap = 16;

    const updateShift = () => {
      const mapEl = document.getElementById("map");
      const zoomEl = mapEl?.querySelector(".map-type-zoom-control");
      if (!zoomEl) return;
      setAnalyticsOpenShift(Math.round(zoomEl.getBoundingClientRect().width + gap));
    };

    updateShift();
    const timeoutIds = [
      window.setTimeout(updateShift, 0),
      window.setTimeout(updateShift, 180),
      window.setTimeout(updateShift, 360),
    ];
    window.addEventListener("resize", updateShift);

    const mapEl = document.getElementById("map");
    let resizeObserver = null;
    if (mapEl && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateShift);
      resizeObserver.observe(mapEl);
    }

    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      window.removeEventListener("resize", updateShift);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [isMobileViewport, showBottomPanel, isRtl]);

  const loadAgentsForModal = useCallback(async () => {
    if (!activityId) return [];
    try {
      const response = await fetchFieldAgentMap(activityId);
      const list = Array.isArray(response?.body?.value) ? response.body.value : Array.isArray(response) ? response : [];
      // Only show field agents who belong to a group assigned to this user
      // (blank group list => show all).
      return filterAgentsByCurrentUserGroups(list);
    } catch (e) {
      console.error("Error fetching agents:", e);
      return [];
    }
  }, [activityId]);

  // Groups are fetched per department (get-by-department); the department comes from localStorage.
  const loadGroupsForModal = useCallback(async () => {
    try {
      const departmentId = Number(
        String(localStorage.getItem("departmentId") ?? "").replace(/"/g, "").trim()
      );
      if (!departmentId || Number.isNaN(departmentId)) return [];
      const list = await getGroupsByDepartment(departmentId);
      // Restrict to the groups the logged-in user has access to (userDetails.groupList).
      return filterGroupsByUserAccess(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("Error fetching groups:", e);
      return [];
    }
  }, []);

  const handleAddAssignee = useCallback(async () => {
    if (!selectedLocation?.tasks?.length) {
      toast.warn(translations[langMode]?.selectWorkflowFirst ?? "Select a marker first.");
      return;
    }
    if (!hasActiveTask) {
      toast.warn(translations[langMode]?.onlyActiveTasksCanBeAssigned ?? "Only Active tasks at this location can be assigned.");
      return;
    }
    if (!activityId) {
      toast.warn(translations[langMode]?.selectWorkflowFirst ?? "Select activity first.");
      return;
    }
    setSelectedAgentIds([]);
    setSelectedGroupId(null);
    const agents = await loadAgentsForModal();
    setAvailableAgents(agents);
    setAvailableGroups(await loadGroupsForModal());
    setShowAssignModal(true);
  }, [selectedLocation, hasActiveTask, activityId, langMode, loadAgentsForModal, loadGroupsForModal]);

  const handleOpenReassignModal = useCallback(async () => {
    if (!selectedLocation?.tasks?.length) {
      toast.warn(translations[langMode]?.selectWorkflowFirst ?? "Select a marker first.");
      return;
    }
    if (!activityId) {
      toast.warn(translations[langMode]?.selectWorkflowFirst ?? "Select activity first.");
      return;
    }
    if (selectedLocation.tasks.some((task) => !canReassignTask(task, activityId))) {
      toast.warn(translations[langMode]?.completedTaskReassignNotAllowed ?? "Only unsuccessful completed tasks can be reassigned.");
      return;
    }
    setSelectedAgentIds([]);
    setSelectedGroupId(null);
    const agents = await loadAgentsForModal();
    setAvailableAgents(agents);
    setAvailableGroups(await loadGroupsForModal());
    setShowReassignModal(true);
  }, [selectedLocation, activityId, langMode, loadAgentsForModal, loadGroupsForModal]);

  const handleRefresh = useCallback(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleRemoveAssignee = useCallback(async () => {
    if (!selectedLocation?.tasks?.length) {
      toast.warn(translations[langMode]?.selectWorkflowFirst ?? "Select a marker first.");
      return;
    }
    const assignees = selectedLocation.tasks.flatMap((t) => t.assignees || []);
    const byId = new Map();
    assignees.forEach((a) => {
      const id = a.userId || a.id;
      if (id && !byId.has(id)) byId.set(id, a);
    });
    const unique = Array.from(byId.values());
    if (unique.length === 0) {
      toast.warn(translations[langMode]?.selectedtasknotassinged ?? "No assignees on selected tasks.");
      return;
    }
    setAssigneesToUnassign(unique);
    setSelectedAssigneesToUnassign([]);
    setSelectedUnassignGroupId(null);
    setAvailableGroups(await loadGroupsForModal());
    setShowUnassignModal(true);
  }, [selectedLocation, langMode, loadGroupsForModal]);

  const handleDelete = useCallback(() => {
    if (!selectedLocation?.tasks?.length) {
      toast.warn(translations[langMode]?.selectWorkflowFirst ?? "Select a marker first.");
      return;
    }
    setDeleteComment("");
    setShowDeleteModal(true);
  }, [selectedLocation, langMode]);

  const toggleAgentSelection = (agentId) => {
    setSelectedAgentIds((prev) =>
      prev.includes(agentId) ? prev.filter((id) => id !== agentId) : [...prev, agentId]
    );
  };

  const handleAssignConfirm = useCallback(async () => {
    if ((selectedAgentIds.length === 0 && selectedGroupId == null) || selectedTaskIds.length === 0 || !activityId) return;
    setActionLoading(true);
    try {
      const data = await assignTasks(selectedTaskIds, selectedAgentIds, activityId, langMode, selectedGroupId);
      if (data?.header?.code === 600) {
        toast.success(data?.body?.value ?? "Tasks assigned.");
        setShowAssignModal(false);
        await refetchAndSyncSelection();
      } else {
        const backendMessage = data?.body?.value ?? data?.body?.error;
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (err) {
      console.error(err);
      const backendMessage = getBackendErrorMessage(err);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setActionLoading(false);
    }
  }, [selectedAgentIds, selectedGroupId, selectedTaskIds, activityId, langMode, refetchAndSyncSelection]);

  const handleReassignConfirm = useCallback(async () => {
    if ((selectedAgentIds.length === 0 && selectedGroupId == null) || selectedTaskIds.length === 0 || !activityId) return;
    setActionLoading(true);
    try {
      const data = await multiassignTasks(selectedTaskIds, selectedAgentIds, activityId, langMode, selectedGroupId);
      if (data?.header?.code === 600) {
        toast.success(data?.body?.value ?? "Tasks reassigned.");
        setShowReassignModal(false);
        await refetchAndSyncSelection();
      } else {
        const backendMessage = data?.body?.value ?? data?.body?.error;
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (err) {
      console.error(err);
      const backendMessage = getBackendErrorMessage(err);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setActionLoading(false);
    }
  }, [selectedAgentIds, selectedGroupId, selectedTaskIds, activityId, langMode, refetchAndSyncSelection]);

  const handleToggleUnassignAssignee = (assigneeId) => {
    setSelectedAssigneesToUnassign((prev) =>
      prev.includes(assigneeId) ? prev.filter((id) => id !== assigneeId) : [...prev, assigneeId]
    );
  };

  const handleToggleAllUnassignAssignees = () => {
    if (selectedAssigneesToUnassign.length === (assigneesToUnassign || []).length) {
      setSelectedAssigneesToUnassign([]);
    } else {
      setSelectedAssigneesToUnassign((assigneesToUnassign || []).map((a) => a.userId));
    }
  };

  const handleUnassignConfirm = useCallback(async () => {
    const isGroupUnassign = selectedUnassignGroupId != null;
    if ((selectedAssigneesToUnassign.length === 0 && !isGroupUnassign) || selectedTaskIds.length === 0 || !activityId) return;
    setActionLoading(true);
    try {
      // Group unassign targets all selected tasks; assignee unassign targets only tasks holding those assignees.
      const taskIdsForUnassign = isGroupUnassign
        ? selectedTaskIds
        : selectedLocation.tasks
          .filter((task) =>
            task.assignees?.some((a) => selectedAssigneesToUnassign.includes(a.userId))
          )
          .map((t) => t.taskId);
      if (taskIdsForUnassign.length === 0) {
        toast.warn("No tasks found for selected assignees.");
        setActionLoading(false);
        return;
      }
      const data = await unassignTask({
        workflowId: parseInt(activityId, 10),
        taskIds: taskIdsForUnassign,
        assigneeId: isGroupUnassign ? null : selectedAssigneesToUnassign,
        groupId: isGroupUnassign ? selectedUnassignGroupId : null,
      });
      if (data?.header?.code === 600) {
        toast.success(translations[langMode]?.unAssignTaskSuccessMeg ?? "Unassigned successfully.");
        setShowUnassignModal(false);
        setAssigneesToUnassign([]);
        setSelectedAssigneesToUnassign([]);
        setSelectedUnassignGroupId(null);
        await refetchAndSyncSelection();
      } else {
        const backendMessage = data?.body?.error ?? data?.body?.value;
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (err) {
      console.error(err);
      const backendMessage = getBackendErrorMessage(err);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setActionLoading(false);
    }
  }, [selectedAssigneesToUnassign, selectedUnassignGroupId, selectedTaskIds, selectedLocation, activityId, langMode, refetchAndSyncSelection]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteComment?.trim()) {
      toast.error(translations[langMode]?.plzenteracommentbeforedel ?? "Please enter a comment.");
      return;
    }
    if (selectedTaskIds.length === 0) return;
    setActionLoading(true);
    try {
      const data = await deleteTasks(selectedTaskIds, deleteComment);
      if (data?.header?.code === 600) {
        toast.success(translations[langMode]?.taskdelsuccessfully ?? "Tasks deleted.");
        setShowDeleteModal(false);
        setDeleteComment("");
        setSelectedLocation(null);
        setShowDetailPanel(false);
        fetchTasks();
      } else {
        const backendMessage = data?.body?.value ?? data?.body?.error;
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (err) {
      console.error(err);
      const backendMessage = getBackendErrorMessage(err);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setActionLoading(false);
    }
  }, [deleteComment, selectedTaskIds, langMode, fetchTasks]);

  const handleToggleDetailPanel = () => {
    setShowDetailPanel(!showDetailPanel);
    // If closing, also clear selected location
    if (showDetailPanel) {
      setSelectedLocation(null);
    }
  };

  const handleToolbarAction = (action) => {
    switch (action) {
      case "print":
        window.print();
        break;
      case "refresh":
        handleRefresh();
        break;
      case "select":
        // TODO: Implement select mode
        console.log("Select mode");
        break;
      case "Draw on Map":
        if (drawnItems) {
          drawnItems.clearLayers();
        }

        // CLOSE DETAIL PANEL ALSO (optional but better UX)
        setSelectedLocation(null);
        setShowDetailPanel(false);
        // Draw tools and Custom Marker shouldn't be open at the same time.
        setShowCustomMarkerModal(false);
        // TODO: Implement edit mode
        setDrawingEnabled((prev) => !prev);
        console.log("Draw on Map");
        break;
      case "calendar":
        // TODO: Implement calendar view
        console.log("Calendar view");
        setShowDatePanel((prev) => !prev);
        break;
      case "chart":
        // Toggle bottom panel - when opening, also expand it
        const newBottomPanelState = !showBottomPanel;
        setShowBottomPanel(newBottomPanelState);
        if (newBottomPanelState) {
          // When opening, also expand the panel to show activity dropdown
          setBottomPanelExpanded(true);
        } else {
          // Reset expanded state when closing
          setBottomPanelExpanded(false);
          setAnalyticsFilters({ status: "", fieldAgentId: "", fromDate: null, toDate: null });
          setTaskStatusContext(null)
          setSelectedAssigneeId('')
        }
        break;
      case "download":
        // TODO: Implement download
        console.log("Download");
        handleShowModal();
        break;
      case "marker-settings":
        if (!workflowIdForTracking) {
          toast.warn(translations[langMode].selectWorkflowFirst);
          return;
        }
        // Draw tools and Custom Marker shouldn't be open at the same time.
        setDrawingEnabled(false);
        setShowCustomMarkerModal(true);
        break;
      case "fullscreen":
        // TODO: Implement fullscreen
        if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen();
        } else {
          document.exitFullscreen();
        }
        break;
      default:
        break;
    }
  };
  const handleListView = () => {
    navigate("/task-manage");
  };

  const handleShowModal = () => {
    if (!activityId) {
      toast.warn(translations[langMode].selectWorkflowFirst);
      return;
    }
    console.log("Download Modal Opened");
    setDownloadModal(true);
  };

  const handleCustomMarkerSaveSuccess = useCallback(
    async ({ workflowId, markerColorList }) => {
      const colorByStatus = (markerColorList || []).reduce((acc, item) => {
        if (item?.taskStatus) {
          acc[item.taskStatus] = item.color;
        }
        return acc;
      }, {});

      setMarkers((prev) =>
        (prev || []).map((task) => {
          const taskWorkflowId = task?.workflowId ?? task?.workflowID ?? task?.activityId;
          const isSameWorkflow =
            workflowId == null ? true : String(taskWorkflowId) === String(workflowId);
          if (!isSameWorkflow) return task;
          const dynamicColor = colorByStatus[task?.taskStatus];
          return dynamicColor ? { ...task, color: dynamicColor } : task;
        })
      );

      // Keep the status filter dots in sync with whatever was just saved.
      setCustomStatusColors((prev) => ({ ...prev, ...colorByStatus }));

      // Keep frontend optimistic update, then sync with backend source of truth.
      await fetchTasks();
    },
    [fetchTasks]
  );

  // Load any already-saved custom marker colors so the status filter dots
  // are correct from the start, not just after the settings modal is used.
  useEffect(() => {
    if (!workflowIdForTracking) {
      setCustomStatusColors({});
      return;
    }

    let cancelled = false;
    (async () => {
      const colorList = await fetchMarkerColors(workflowIdForTracking);
      if (cancelled) return;
      const colorByStatus = (colorList || []).reduce((acc, item) => {
        if (item?.taskStatus) {
          acc[item.taskStatus] = item.color;
        }
        return acc;
      }, {});
      setCustomStatusColors(colorByStatus);
    })();

    return () => {
      cancelled = true;
    };
  }, [workflowIdForTracking]);

  const downloadFiltration = async (selectedOption) => {
    setIsDownloading(true);
    setIsLoading(true);
    
    const formattedFromDate = formatDateYYYYMMDD(fromDate);
    const formattedToDate = formatDateYYYYMMDD(toDate);
    const activityAnalyticsDateFormattedFromDate = formatDateYYYYMMDD(activityAnalyticsDate?.fromDate);
    const activityAnalyticsDateFormattedToDate = formatDateYYYYMMDD(activityAnalyticsDate?.toDate);

    try {
      const userTypeCookie = Cookies.get("userType");
      const params = {
        workflowIds: activityId || "",
        priority: priority || "",
        active: taskStatusContext || "",
        areaCode: "",
        accountNo: acc || "",
        assigneeId: selectedAssigneeId || "",
        organizationId: "",
        projectId: projectId || "",
        fromDate : formattedFromDate || activityAnalyticsDateFormattedFromDate,
        toDate : formattedToDate || activityAnalyticsDateFormattedToDate
      };

      const orgId = JSON.parse(localStorage.getItem("selectedOrgId"));
      if (selectedOption === "all") {
        params.organizationId = orgId || "";
        params.workflowIds = "";
      } else if (
        selectedOption === "particular" ||
        userTypeCookie !== "super_admin"
      ) {
        params.organizationId = selectedOrgId || "";

        params.workflowIds = activityId || "";
      }
      const data = await fetchFilterExcel({
        workflowIds: params.workflowIds,
        priority: params.priority,
        active: params.active,
        areaCode: params.areaCode,
        accountNo: params.accountNo,
        assigneeId: params.assigneeId,
        organizationId: params.organizationId,
        projectId: params.projectId,
        includeProjectId: selectedOption === "all",
        fromDate : params.fromDate,
        toDate : params.toDate
      });
      const code = data.header?.code;
      if (code === 600) {
        const s3Link = data.body?.value;
        const link = document.createElement("a");
        link.href = s3Link;
        link.setAttribute("download", "report.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(translations[langMode].excelisdownloading);
      } else {
        toast.error(data?.body?.value || translations[langMode].norecordfound);
      }
    } catch (error) {
      console.error("Error during report download:", error);
      const backendMessage = getBackendErrorMessage(error);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setIsLoading(false);
      setIsDownloading(false);
    }
  };

  // Close calendar when clicking close icon or anywhere
  useEffect(() => {
    const close = (e) => {
      const calendar = document.getElementById("calendar-popover");
      // The date pickers render their day grid in a separate portaled popper, so a click on a
      // day is "outside" #calendar-popover. Without this check, choosing the From date closed
      // the panel before the To date could be picked. composedPath is read at dispatch time, so
      // it still holds the picker even after the clicked day has been re-rendered away.
      const path = typeof e.composedPath === "function" ? e.composedPath() : [];
      const clickedInsideDatePicker = path.some(
        (node) => node?.classList?.contains("react-datepicker") || node?.classList?.contains("react-datepicker-popper")
      );
      if (calendar && !calendar.contains(e.target) && !clickedInsideDatePicker) {
        setShowCalendar(false);
        setFromDate(null)
        setToDate(null)
      }
    };
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  // Measure date panel height for responsive offsets
  useEffect(() => {
    if (!showDatePanel) {
      setDatePanelHeight(0);
      return;
    }
    const el = datePanelRef.current;
    if (!el) {
      setDatePanelHeight(0);
      return;
    }
    const update = () => {
      const next = Math.round(el.getBoundingClientRect().height);
      setDatePanelHeight((prev) => (prev !== next ? next : prev));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [showDatePanel]);

  // auto-detect range from manual selection
  useEffect(() => {
    if (selectedDates.length === 30) setActiveRange(30);
    else if (selectedDates.length === 60) setActiveRange(60);
    else if (selectedDates.length === 90) setActiveRange(90);
    else setActiveRange(null);
  }, [selectedDates]);

  useEffect(() => {
    if (!showDatePanel) {
       setShowCalendar(false);
        setFromDate(null)
        setToDate(null)
    }
  }, [showDatePanel])

  useEffect(() => {
    if (!showBottomPanel) {
        setTaskStatusContext(null)
        setSelectedAssigneeId('')
        setActivityAnalyticsDate({ fromDate: null, toDate: null });
        setAnalyticsFilters({ status: "", fieldAgentId: "", fromDate: null, toDate: null });
    }
  }, [showBottomPanel, setTaskStatusContext]);

     useEffect(() => {
      setIsLoading(true);
  
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 500); 
  
      return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
      const map = document.getElementById("map")
      if (!map) return;

      if(showDatePanelInMap) {
        map.classList.add("map-with-date-panel")
      }else{
        map.classList.remove("map-with-date-panel")
      }
    }, [showDatePanelInMap])

  const mapDatePanelContent = (
    <>
      <div
        ref={datePanelRef}
        className={`absolute top-0 left-0 w-full
  flex items-center gap-4 overflow-hidden
  transition-all duration-300 ease-in-out transform
  ${showDatePanel
            ? "max-h-[120px] opacity-100 translate-y-0 scale-100 pointer-events-auto sm:p-5 p-4 border-b shadow-sm z-10"
            : "max-h-0 opacity-0 -translate-y-3 scale-95 pointer-events-none z-0"
          }
  ${mapType === "satellite" ? 'bg-[#fffffff2] backdrop-blur-md' : 'bg-[#ffffffcc]/80 backdrop-blur-md'}
        }`}
      >
        <button
          className="map-control-btn flex-shrink-0"
          title={translations[langMode]?.mapTipPrevWeek ?? "Previous Week"}
          onClick={() => moveDate(-1)}
        >
          <ChevronLeft className="w-4 h-4 text-[#0f1729]" />
        </button>

        <button
          className="date-chip flex items-center gap-2 flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            setShowCalendar((p) => !p);
          }}
        >
          <Calendar className="w-5 h-5 text-blue-800" />
          <span className="hidden md:inline">Previous Dates</span>
        </button>
        <div className="flex items-center gap-3 flex-1 overflow-x-auto scrollbar-hide">
          <div className="flex items-center gap-3 min-w-max">
            {getVisibleDates().map((date) => (
              <button
                key={date.toISOString()}
                className={`flex items-center gap-2 whitespace-nowrap transition-all duration-300 flex-shrink-0 ${
                  isSelected(date) ? "date-chip-active" : "date-chip"
                }`}
                onClick={() => toggleDate(date)}
              >
                {format(date)}
                {isSelected(date) && (
                  <div className="w-5 h-5 rounded-full bg-[#2463eb] flex items-center justify-center">
                    <Check className="w-3 h-3 text-[#f8fafc]" />
                  </div>
                )}
              </button>
            ))}
            <button
              className="map-control-btn flex-shrink-0"
              title={translations[langMode]?.mapTipNextWeek ?? "Next Week"}
              onClick={() => moveDate(1)}
            >
              <ChevronRight className="w-4 h-4 text-[#0f1729]" />
            </button>
            {[30, 60, 90].map((days) => (
              <button
                key={days}
                className={`flex items-center gap-2 flex-shrink-0 ${activeRange === days ? "date-chip-active" : "date-chip"}`}
                onClick={() => toggleLastDays(days)}
              >
                <span>{days} Days</span>
                {activeRange === days && (
                  <div className="w-5 h-5 rounded-full bg-[#2463eb] flex items-center justify-center">
                    <Check className="w-3 h-3 text-[#f8fafc]" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        <ModalCloseButton
          ariaLabel={translations[langMode]?.mapTipHideDates ?? "Hide date navigation"}
          className="h-8 w-8 flex-shrink-0"
          size={16}
          onClick={() => {
            setShowDatePanel(false);
            setShowCalendar(false);
            setFromDate(null);
            setToDate(null);
          }}
          title={translations[langMode]?.mapTipHideDates ?? "Hide date navigation"}
        />
      </div>

      {showCalendar && (
        <div
          id="calendar-popover"
          className={`${
            isRtl ? "absolute sm:top-16 top-14 mt-2 sm:right-16 right-0" : "absolute sm:top-16 top-14 mt-2 sm:left-16 msm:left-10 left-0"
          } z-[2000] bg-white rounded-lg border shadow-lg p-3 flex flex-col gap-3`}
        >
          {/* FROM DATE */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500">{translations[langMode].fromdate}</label>
            <DatePicker
              selected={fromDate}
              onChange={(date) => {
                setFromDate(date);
                if (toDate && date && toDate < date) {
                  setToDate(null);
                }
              }}
              placeholderText={translations[langMode]?.mapSelectFromDate ?? "Select from date"}
              minDate={minDate}
              maxDate={today}
              selectsStart
              startDate={fromDate}
              endDate={toDate}
              className="w-full h-9 px-2 !rounded-lg text-sm border border-gray-300"
              calendarClassName="!rounded-lg shadow-lg"
              popperPlacement={isRtl ? "bottom-end" : "bottom-start"}
              popperContainer={({ children }) => (
                <div style={{ position: "fixed", zIndex: 100000 }}>
                  {children}
                </div>
              )}
            />
          </div>

          {/* TO DATE */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500">{translations[langMode].todate}</label>
            <DatePicker
              selected={toDate}
              onChange={(date) => {
                setToDate(date);
                // Both dates are chosen: close the picker panel (the range stays applied).
                if (date && fromDate) setShowCalendar(false);
              }}
              placeholderText={translations[langMode]?.mapSelectToDate ?? "Select to date"}
              minDate={fromDate || minDate}
              maxDate={today}
              selectsEnd
              startDate={fromDate}
              endDate={toDate}
              disabled={!fromDate}
              className="w-full h-9 px-2 !rounded-lg text-sm border border-gray-300"
              calendarClassName="!rounded-lg shadow-lg"
              popperPlacement={isRtl ? "bottom-end" : "bottom-start"}
              popperContainer={({ children }) => (
                <div style={{ position: "fixed", zIndex: 100000 }}>
                  {children}
                </div>
              )}
            />
          </div>
        </div>
      )}
    </>
  );

  const CalendarFieldInput = React.forwardRef(
    ({ value, onClick, placeholder, disabled }, ref) => (
      <button
        type="button"
        ref={ref}
        onClick={onClick}
        disabled={disabled}
        className={`flex h-12 w-full items-center rounded-xl border border-gray-300 bg-white px-4 text-left text-sm ${
          disabled ? "cursor-not-allowed opacity-60" : ""
        }`}
      >
        <span className={`truncate ${value ? "text-slate-700" : "text-slate-400"}`}>
          {value || placeholder || "dd-mm-yyyy"}
        </span>
        <span className="ml-auto inline-flex h-full w-10 items-center justify-center border-l border-gray-200 text-black">
          <Calendar size={16} />
        </span>
      </button>
    )
  );
  CalendarFieldInput.displayName = "CalendarFieldInput";

  const headerDatePanelContent = (
    <div className="relative rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-[0_16px_30px_rgba(15,23,42,0.08)]">
      <ModalCloseButton
        ariaLabel={translations[langMode]?.mapTipCloseCalendar ?? "Close calendar panel"}
        className="absolute right-3 top-3 h-8 w-8"
        size={16}
        onClick={() => {
          setShowDatePanel(false);
          setShowCalendar(false);
          setFromDate(null);
          setToDate(null);
        }}
        title={translations[langMode]?.mapTipCloseCalendar ?? "Close calendar panel"}
      />
      <div className="flex flex-wrap items-center gap-3">
        {getVisibleDates().map((date) => (
          <button
            key={date.toISOString()}
            className={`flex items-center gap-2 whitespace-nowrap transition-all duration-300 ${isSelected(date) ? "date-chip-active" : "date-chip"}`}
            onClick={() => toggleDate(date)}
          >
            {format(date)}
            {isSelected(date) && (
              <div className="w-5 h-5 rounded-full bg-[#2463eb] flex items-center justify-center">
                <Check className="w-3 h-3 text-[#f8fafc]" />
              </div>
            )}
          </button>
        ))}
        <button
          className="map-control-btn"
          title={translations[langMode]?.mapTipNextWeek ?? "Next Week"}
          onClick={() => moveDate(1)}
        >
          <ChevronRight className="w-4 h-4 text-[#0f1729]" />
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {[30, 60, 90].map((days) => (
          <button
            key={days}
            className={`flex items-center gap-2 ${activeRange === days ? "date-chip-active" : "date-chip"}`}
            onClick={() => toggleLastDays(days)}
          >
            <span>{days} Days</span>
            {activeRange === days && (
              <div className="w-5 h-5 rounded-full bg-[#2463eb] flex items-center justify-center">
                <Check className="w-3 h-3 text-[#f8fafc]" />
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500">{translations[langMode].fromdate}</label>
          <DatePicker
            selected={fromDate}
            onChange={(date) => {
              setFromDate(date);
              if (toDate && date && toDate < date) {
                setToDate(null);
              }
            }}
            placeholderText="dd-mm-yyyy"
            minDate={minDate}
            maxDate={today}
            selectsStart
            startDate={fromDate}
            endDate={toDate}
            customInput={<CalendarFieldInput />}
            calendarClassName="!rounded-lg shadow-lg"
            popperPlacement={isRtl ? "top-end" : "top-start"}
            popperContainer={({ children }) => (
              <div style={{ position: "fixed", zIndex: 100000 }}>
                {children}
              </div>
            )}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500">{translations[langMode].todate}</label>
          <DatePicker
            selected={toDate}
            onChange={(date) => setToDate(date)}
            placeholderText="dd-mm-yyyy"
            minDate={fromDate || minDate}
            maxDate={today}
            selectsEnd
            startDate={fromDate}
            endDate={toDate}
            disabled={!fromDate}
            customInput={<CalendarFieldInput />}
            calendarClassName="!rounded-lg shadow-lg"
            popperPlacement={isRtl ? "top-end" : "top-start"}
            popperContainer={({ children }) => (
              <div style={{ position: "fixed", zIndex: 100000 }}>
                {children}
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );

  const handleTrackingToggle = () => {
    // Tracking is temporarily disabled - logic kept below (not deleted) so
    // it can be switched back on later, just showing a "coming soon" toast
    // for now instead of actually toggling tracking.
    // if (isTrackingEnabled) {
    //   // If tracking is enabled, disable it (hide tracking lines) and close the panel
    //   setIsTrackingEnabled(false);
    //   setTrackingData([]);
    //   setShowTrackingPanel(false);
    // } else {
    //   // If tracking is disabled, enable it AND show filter panel
    //   setIsTrackingEnabled(true);
    //   setShowTrackingPanel(true);
    // }
    toast.info(translations[langMode]?.trackingComingSoon ?? "Tracking is coming soon - stay tuned!");
  };

  // Shared `bottom` offset for the Analytics toggle and the Task Actions bar
  // beside it - both computed identically so they always stay level with
  // each other as one row. Previously the Task Actions bar used its own
  // fixed 32/82/282px values while Analytics additionally clamped against
  // analyticsMaxBottomOffset (to avoid rising into the control rail/zoom
  // stack on a short map) - on tall/normal maps that clamp never binds, so
  // both matched by coincidence, but on a short desktop viewport it does
  // bind and only Analytics moved, breaking the alignment. Reusing this one
  // value for both keeps them pinned together at every viewport height.
  //
  // Closed baseline is 16 (not 32), matching the 16px edge gap the top
  // filter bar sits at (`top-4`) - on normal/tall maps the clamp never
  // binds so this is the value actually used; on a short desktop the clamp
  // already pulls it down further and that behavior is unchanged here.
  const bottomPanelHeight = isMobileViewport ? 360 : 260;
  const analyticsAndTaskBarBottom = Math.max(
    showBottomPanel ? (bottomPanelExpanded ? bottomPanelHeight : 42) + 8 : 0,
    Math.min(
      showBottomPanel ? (bottomPanelExpanded ? bottomPanelHeight + 12 : 82) : 16,
      analyticsMaxBottomOffset ?? Infinity
    )
  );

  return (
    <div className="relative w-full h-full min-h-0" style={{ zIndex: 0 }}>
    {isLoading && <Spinner />}
      {headerActivitySlotEl && activitySelectorControl
        ? createPortal(
          <div className="w-full">{activitySelectorControl}</div>,
          headerActivitySlotEl
        )
        : null}
      {headerCalendarTriggerSlotEl
        ? createPortal(
          <div className="w-full">{calendarHeaderTriggerControl}</div>,
          headerCalendarTriggerSlotEl
        )
        : null}
      {headerExportSlotEl
        ? createPortal(exportHeaderButtonControl, headerExportSlotEl)
        : null}
      {showDatePanel && shouldRenderCalendarInHeader && headerCalendarPanelStyle && typeof document !== "undefined"
        ? createPortal(<div style={headerCalendarPanelStyle}>{headerDatePanelContent}</div>, document.body)
        : null}
      <div
        className={`flex h-full min-h-0 transition-all duration-300 ${showDetailPanel ? "gap-0" : "gap-0"}`}
      >
        {/* Map Container - adjusts width when detail panel is open */}
        <div
          className={`transition-all duration-300 relative w-full h-full min-h-0`}
        >
          {/* DATE PANEL */}
          {showDatePanelInMap ? mapDatePanelContent : null}

          {/* MAP */}
          <MapContainer
            center={[21.512, 55.923]}
            zoom={5}
            className="relative"
            zoomControl={false}
            id="map"

            style={{
              height: showBottomPanel
                ? bottomPanelExpanded
                  ? `calc(100% - ${bottomPanelHeight}px)`
                  : "calc(100% - 42px)"
                : "100%",
              minHeight: isMobileViewport ? "320px" : undefined,
              width: "100%",
              overflow: "hidden",
              borderTopLeftRadius: "16px",
              borderBottomLeftRadius: "16px",
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              marginBottom: "9px",
              marginTop: showDatePanelInMap ? `${datePanelOffset}px` : "0px",
              transition: "margin-top 0.3s ease",
              zIndex: 0,
            }}

            >
            {/* RIGHT SIDE BUTTONS - Activity dropdown + Tracking + Map view + List view - move left when detail panel is open */}
            <div
              className={`map-top-left-toolbar absolute px-1 top-4 z-[1000] flex flex-wrap items-center gap-3 transition-all duration-300 max-sm:items-stretch max-sm:gap-2 ${
                isRtl ? "flex-row-reverse" : ""
              }`}
              style={{
                top: showDatePanelInMap ? (isMobileViewport ? "92px" : "100px") : (isMobileViewport ? "12px" : "16px"),
                left: isRtl ? "auto" : (isMobileViewport ? "12px" : "16px"),
                right: isRtl ? (isMobileViewport ? "12px" : "16px") : (isMobileViewport ? "76px" : "auto"),
                maxWidth: isMobileViewport ? "calc(100% - 24px)" : "calc(100% - 190px)",
              }}
            >
              <div className="inline-flex h-8 lg:h-10 items-center gap-1 rounded-xl border border-gray-200 bg-white p-0.5 lg:p-1 shadow-md max-sm:h-9 max-sm:p-0.5">
                <Tooltip text={translations[langMode]?.mapTipMapView ?? "Map view"} position="bottom">
                  <button
                    type="button"
                    className="flex h-7 w-9 lg:h-8 lg:w-[42px] items-center justify-center rounded-lg bg-[#236DB4] text-white shadow-sm transition-all duration-200 max-sm:h-7 max-sm:w-10"
                    aria-label={translations[langMode]?.mapTipMapView ?? "Map view"}
                  >
                    <svg width="18" height="18" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M14.0813 5.6259L7.90625 1.67578L1.375 6.1875V20.625L7.91871 16.3741L14.0938 20.3242L20.625 15.8125V1.375L14.0813 5.6259ZM13.4062 18.0898L8.59375 14.9961V3.91016L13.4062 7.00391V18.0898Z" fill="currentColor" />
                    </svg>
                  </button>
                </Tooltip>

                <Tooltip text={translations[langMode]?.mapTipListView ?? "List view"} position="bottom">
                  <button
                    type="button"
                    onClick={handleListView}
                    className="flex h-7 w-9 lg:h-8 lg:w-[42px] items-center justify-center rounded-lg bg-white text-[#236DB4] transition-all duration-200 hover:bg-blue-50 max-sm:h-7 max-sm:w-10"
                    aria-label={translations[langMode]?.mapTipListView ?? "List view"}
                  >
                    <List size={16} className="lg:hidden" />
                    <List size={18} className="hidden lg:block" />
                  </button>
                </Tooltip>
              </div>

              <MapAccountSearch
                activityId={activityId}
                groupAllowedAgentIds={groupAllowedAgentIds}
                langMode={langMode}
                onHighlight={handleAccountHighlight}
                onResult={handleAccountSearchResult}
                onClear={handleAccountSearchClear}
              />

              {/* Task Status filter pills - moved here from the DetailPanel's
                  "Task Status" section; same handlers/state/counts as before
                  (handleStatusClick, selectedStatusFilter, taskStatusCounts),
                  plus a new "All" pill that clears the status filter. Height
                  matches the Map/List toggle above so both groups read as one
                  consistent row. */}
              <div className="flex flex-wrap items-center gap-1.5">
                <Tooltip text={translations[langMode]?.mapTipShowAll ?? "Show all tasks"} position="bottom">
                  <button
                    type="button"
                    onClick={handleAllStatusClick}
                    className={`h-8 lg:h-10 px-2 rounded-xl text-xs lg:text-sm font-medium transition-colors max-sm:h-9 max-sm:px-2 max-sm:text-sm ${
                      !selectedStatusFilter
                        ? "bg-[#236DB4] text-white"
                        : "bg-white text-gray-700 border border-gray-200 hover:bg-blue-50"
                    }`}
                  >
                    {translations[langMode]?.allStatusLabel ?? "All"}{" "}
                    {TASK_STATUS_PILL_ORDER.reduce((sum, s) => sum + (taskStatusCounts[s] ?? 0), 0)}
                  </button>
                </Tooltip>
                {TASK_STATUS_PILL_ORDER.map((status) => {
                  const isSelected = selectedStatusFilter === status;
                  // customStatusColors is keyed by the API status format
                  // (e.g. "In_Progress"), same as markerColorList/task.taskStatus
                  // - status here is the display format ("In Progress"), so it
                  // must be converted before the lookup or it silently misses.
                  const dotColor =
                    customStatusColors[displayStatusToApiStatus(status)] ?? getTaskStatusDotColor(status);
                  const statusLabel = getTaskStatusPillLabel(langMode, status);
                  return (
                    <Tooltip key={status} text={(translations[langMode]?.mapTipShowOnly ?? "Show only {status} tasks").replace("{status}", statusLabel)} position="bottom">
                      <button
                        type="button"
                        onClick={() => handleStatusClick(status)}
                        className={`h-8 lg:h-10 px-2 rounded-xl text-xs lg:text-sm font-medium transition-colors flex items-center gap-1.5 max-sm:h-9 max-sm:px-3 max-sm:text-sm ${
                          isSelected
                            ? "bg-[#236DB4] text-white"
                            : "bg-white text-gray-700 border border-gray-200 hover:bg-blue-50"
                        }`}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: dotColor }}
                        />
                        {statusLabel} {taskStatusCounts[status] ?? 0}
                      </button>
                    </Tooltip>
                  );
                })}
              </div>

              {SHOW_TRACKING_BUTTON && isMobileViewport && (
                <button
                type="button"
                onClick={handleTrackingToggle}
                className={`py-[5px] px-3 h-9 rounded-lg border transition-colors max-sm:h-8 max-sm:px-2 max-sm:max-w-[108px] ${isTrackingEnabled
                    ? "bg-[#236DB4] text-white border-[#236DB4]"
                    : "bg-white text-[#236DB4] border-[#236DB4] hover:bg-blue-50"
                  }`}
              >
                <span className="text-sm font-medium whitespace-nowrap max-sm:text-xs max-sm:truncate">{translations[langMode]?.tracking ?? "Tracking"}</span>
              </button>
              )}
            </div>

            {!isMobileViewport && (
              <div
                className="map-tracking-anchor absolute z-[1005] transition-[left,right] duration-300"
                style={{
                  top: showDatePanelInMap ? "100px" : "16px",
                  right: isRtl ? "auto" : (showDetailPanel ? "416px" : "16px"),
                  left: isRtl ? (showDetailPanel ? "416px" : "16px") : "auto",
                }}
              >
                {SHOW_TRACKING_BUTTON && (
                <Tooltip
                  text={translations[langMode]?.tracking ?? "Tracking"}
                  position="left"
                  disabled={!showDetailPanel}
                >
                  <button
                    type="button"
                    onClick={handleTrackingToggle}
                    className={`flex items-center justify-center h-8 lg:h-10 rounded-xl border transition-colors max-sm:h-9 ${
                      showDetailPanel ? "w-8 lg:w-10 px-0" : "px-3 lg:px-4"
                    } ${
                      isTrackingEnabled
                        ? "bg-[#236DB4] text-white border-[#236DB4]"
                        : "bg-white text-[#236DB4] border-[#236DB4] hover:bg-blue-50"
                    }`}
                  >
                    {showDetailPanel ? (
                      <>
                        <Navigation size={16} className="lg:hidden" />
                        <Navigation size={18} className="hidden lg:block" />
                      </>
                    ) : (
                      <span className="text-xs lg:text-sm font-medium whitespace-nowrap">
                        {translations[langMode]?.tracking ?? "Tracking"}
                      </span>
                    )}
                  </button>
                </Tooltip>
                )}
              </div>
            )}

            {/* Tracking filters panel - clean card design */}
            {showTrackingPanel && (
              <div
                className="absolute z-[1000] w-full max-w-xl transition-all duration-200"
                style={{
                  top: showDatePanelInMap ? "156px" : "60px",
                  left: isMobileViewport
                    ? (isRtl ? "auto" : "76px")
                    : (isRtl ? (showDetailPanel ? "416px" : "16px") : "auto"),
                  right: isMobileViewport
                    ? (isRtl ? "76px" : "auto")
                    : (isRtl ? "auto" : (showDetailPanel ? "416px" : "16px")),
                }}
              >
                <div
                  className="rounded-lg border border-gray-200/90 bg-white shadow-lg backdrop-blur-sm"
                  style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.1)" }}
                >
                  <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#236DB4]/10 text-[#236DB4]">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                        </svg>
                      </span>
                      <h3 className="text-sm font-semibold text-gray-800">{translations[langMode]?.tracking ?? "Tracking"}</h3>
                      {trackingData.length > 0 && (
                        <span className="rounded-full bg-[#236DB4]/15 px-2.5 py-0.5 text-xs font-medium text-[#236DB4]">
                          {trackingData.length} {translations[langMode]?.records ?? "records"}
                        </span>
                      )}
                    </div>
                    <ModalCloseButton
                      ariaLabel="Close tracking panel"
                      onClick={() => {
                        // Close filter panel only, don't disable tracking
                        setShowTrackingPanel(false);
                        setIsTrackingEnabled(false);
                        setTrackingData([]);
                      }}
                    />
                  </div>
                  <div className="p-5">
                    <p className="text-xs text-gray-500 mb-4">Optional: filter by {translations[langMode]?.fieldAgent ?? "Field Agent"} or {translations[langMode]?.date ?? "Date"}</p>
                    <div className="flex flex-nowrap items-end gap-4 overflow-x-auto">
                      <div className="min-w-[140px] w-[180px] shrink-0">
                        <label className="mb-1.5 block text-xs font-medium text-gray-600">{translations[langMode]?.fieldAgent ?? "Field Agent"}</label>
                        <ReactSelector
                          options={[
                            { value: "", label: translations[langMode].allFieldAgents },
                            ...(trackingFieldAgentList || []).map((agent) => {
                              const id = agent.userId ?? agent.id;
                              const name = agent.name ?? agent.userName ?? agent.firstName ?? `Agent ${id}`;
                              return { value: String(id), label: name };
                            }),
                          ]}
                          value={(() => {
                            const opts = [
                              { value: "", label: translations[langMode].allFieldAgents },
                              ...(trackingFieldAgentList || []).map((agent) => {
                                const id = agent.userId ?? agent.id;
                                const name = agent.name ?? agent.userName ?? agent.firstName ?? `Agent ${id}`;
                                return { value: String(id), label: name };
                              }),
                            ];
                            return opts.find((o) => o.value === String(trackingFieldAgentId)) ?? opts[0];
                          })()}
                          onChange={(opt) => setTrackingFieldAgentId(opt?.value ?? "")}
                          isClearable
                          placeholder={translations[langMode]?.select ?? "Select"}
                          height="36px"
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                        />
                      </div>
                      <div className="min-w-[140px] w-[200px] shrink-0">
                        <label className="mb-1.5 block text-xs font-medium text-gray-600">{translations[langMode]?.date ?? "Date"}</label>
                        <DatePicker
                          selected={trackingDate ? new Date(trackingDate) : null}
                          onChange={(d) => setTrackingDate(d ? moment(d).format("YYYY-MM-DDTHH:mm:ss") : "")}
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          dateFormat="yyyy-MM-dd HH:mm"
                          placeholderText={translations[langMode]?.selectDate ?? "Select date"}
                          popperPlacement={isRtl ? "bottom-end" : "bottom-start"}
                          className="w-full h-9 rounded-lg border border-gray-300 px-3 text-sm focus:border-[#236DB4] focus:outline-none focus:ring-1 focus:ring-[#236DB4]"
                          isClearable
                        />
                      </div>
                      <button data-button-intent="primary"
                        type="button"
                        onClick={loadTracking}
                        disabled={trackingLoading}
                        className="h-9 w-[120px] shrink-0 rounded-lg bg-[#236DB4] px-5 text-sm font-medium text-white shadow-sm hover:bg-[#1d5d9e] focus:outline-none focus:ring-2 focus:ring-[#236DB4] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-[#236DB4] transition-colors flex items-center justify-center"
                      >
                        {trackingLoading ? (
                          <>
                            <svg className="h-4 w-4 animate-spin shrink-0" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <span className="ml-2 truncate">{translations[langMode]?.loading ?? "Loading..."}</span>
                          </>
                        ) : (
                          <span className="truncate">{translations[langMode]?.apply ?? "Apply"}</span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {mapType === "map" && (
              <TileLayer
                attribution="© OpenStreetMap contributors"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            )}

            {mapType === "satellite" && (
              <TileLayer
                url="https://mt.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
                attribution='&copy; <a href="https://www.google.com/maps">Google Maps</a>'
              />
            )}
            {/* <AzureZoomControl /> */}
            <AzureZoomControl
              showZoomControl={false}
              showDatePanel={showDatePanelInMap}
              datePanelOffset={datePanelOffset}
              showDetailPanel={showDetailPanel}
              showBottomPanel={showBottomPanel}
              bottomPanelExpanded={bottomPanelExpanded}
              drawingEnabled={drawingEnabled}
              isRtl={isRtl}
            />
            <InvalidateMapSize
              showBottomPanel={showBottomPanel}
              bottomPanelExpanded={bottomPanelExpanded}
              showDatePanel={showDatePanelInMap}
              showDetailPanel={showDetailPanel}
            />
            <MobileMapStabilizer enabled={isMobileViewport} />

            <MarkerClusterComponent
              markers={visibleMarkers}
              onMarkerClick={handleMarkerClick}
              highlightTaskIds={searchedTaskIds}
              focusHighlight={searchPinsAllOnMap}
            />
            <SearchedTaskMarkers tasks={hiddenSearchedTasks} onClick={handleSearchedPinClick} />
            {isTrackingEnabled && <TrackingPolylines trackingData={trackingData} />}

            {/* Map View Toolbar */}
            <MapViewToolbar
              onPrint={() => handleToolbarAction("print")}
              onRefresh={() => handleToolbarAction("refresh")}
              onSelect={() => handleToolbarAction("select")}
              onEdit={() => handleToolbarAction("Draw on Map")}
              onCalendar={() => handleToolbarAction("calendar")}
              onChart={() => handleToolbarAction("chart")}
              onMarkerSettings={() => handleToolbarAction("marker-settings")}
              onDownload={() => handleToolbarAction("download")}
              onFullScreen={() => handleToolbarAction("fullscreen")}
              onToggleDetailPanel={handleToggleDetailPanel}
              isDetailPanelOpen={showDetailPanel}
              onToggleBottomPanel={() => handleToolbarAction("chart")}
              isBottomPanelOpen={showBottomPanel}
              isBottomPanelExpanded={bottomPanelExpanded}
              bottomOffset={analyticsAndTaskBarBottom}
              showDatePanel={showDatePanelInMap}
              drawingEnabled={drawingEnabled}
              showCustomMarkerModal={showCustomMarkerModal}
              isRtl={isRtl}
              isMobileViewport={isMobileViewport}
            />
            <MapTypeControl
              mapType={mapType}
              setMapType={setMapType}
              isRtl={isRtl}
              isMobileViewport={isMobileViewport}
              showDatePanel={showDatePanelInMap}
            />

            {/* Analytics toggle - standalone floating button; opens the same Activity Analytics
                drawer as before (handleToolbarAction("chart")), just repositioned so it always
                stays above the drawer instead of living inside the right-side icon rail. */}
            <div
              className="map-analytics-toggle-anchor absolute z-[1006] transition-all duration-300"
              style={{
                right: isRtl
                  ? "auto"
                  : `${(showDetailPanel ? 416 : (isMobileViewport ? 12 : 16)) + (showBottomPanel && !isMobileViewport ? analyticsOpenShift : 0)}px`,
                left: isRtl
                  ? `${(showDetailPanel ? 416 : (isMobileViewport ? 12 : 16)) + (showBottomPanel && !isMobileViewport ? analyticsOpenShift : 0)}px`
                  : "auto",
                bottom: `${analyticsAndTaskBarBottom}px`,
              }}
            >
              <Tooltip text={translations[langMode]?.activityPanel ?? "Activity Panel"} position="top">
                <button
                  type="button"
                  onClick={() => handleToolbarAction("chart")}
                  className={`map-analytics-toggle-btn flex items-center gap-1.5 h-[42px] lg:h-[46px] rounded-xl border px-3 lg:px-4 shadow-md transition-colors max-sm:px-3 ${
                    showBottomPanel
                      ? "bg-[#236DB4] text-white border-[#236DB4]"
                      : "bg-white text-[#236DB4] border-[#236DB4] hover:bg-blue-50"
                  }`}
                  aria-label={translations[langMode]?.analyticsLabel ?? "Analytics"}
                >
                  <TbWaveSawTool size={16} />
                  <span className="text-xs lg:text-sm font-medium whitespace-nowrap max-sm:hidden">
                    {translations[langMode]?.analyticsLabel ?? "Analytics"}
                  </span>
                  {showBottomPanel ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                </button>
              </Tooltip>
            </div>

            {/* Task Actions bar - Assign/Reassign/Unassign/Delete, moved here from the
                DetailPanel header so they're available directly on the map. Same handlers
                and permission gating as before (handleAddAssignee, handleOpenReassignModal,
                handleRemoveAssignee, handleDelete) - only the location moved. */}
            {showDetailPanel && (
              <div
                className="absolute z-[1004] transition-all duration-300"
                style={{
                  // Anchored the same way as the Analytics toggle (single edge +
                  // bottom), sitting immediately beside it with the same small gap
                  // used elsewhere in this file, instead of centering across the
                  // whole map width.
                  right: isRtl ? "auto" : `${taskActionsBarEdgeOffset}px`,
                  left: isRtl ? `${taskActionsBarEdgeOffset}px` : "auto",
                  bottom: `${analyticsAndTaskBarBottom}px`,
                  maxWidth: "calc(100% - 32px)",
                }}
              >
                <div className="inline-flex flex-wrap max-w-full items-center gap-1 rounded-xl border border-[#d7dfea] bg-white/95 backdrop-blur-sm shadow-lg px-1.5 lg:px-2 py-1 lg:py-1">
                  {selectedTaskIds.length > 0 && (
                    <>
                      <div className="flex items-center gap-1.5 lg:gap-2 px-1.5 lg:px-2">
                        <span className="flex h-6 min-w-[24px] items-center justify-center whitespace-nowrap rounded-full bg-[#236DB4] px-2 text-xs font-semibold text-white">
                          {selectedTaskIds.length}
                        </span>
                        <span className="hidden lg:inline text-sm font-medium text-gray-700 whitespace-nowrap">
                          {translations[langMode]?.selectedCountLabel ?? "selected"}
                        </span>
                      </div>
                      <span aria-hidden="true" className="mx-0.5 lg:mx-1 h-6 w-px bg-[#e7edf6]" />
                    </>
                  )}
                  <Tooltip text={translations[langMode]?.assign} position="top">
                    <button
                      onClick={handleAddAssignee}
                      disabled={!hasActiveTask || !canUpdateTask}
                      className={taskActionClass(hasActiveTask && canUpdateTask)}
                    >
                      <FiUserPlus size={16} />
                      <span className="hidden lg:inline">{translations[langMode]?.assign}</span>
                    </button>
                  </Tooltip>
                  <Tooltip text={translations[langMode]?.reassign} position="top">
                    <button
                      onClick={handleOpenReassignModal}
                      disabled={!canUpdateTask}
                      className={taskActionClass(canUpdateTask)}
                    >
                      <LuRefreshCw size={16} />
                      <span className="hidden lg:inline">{translations[langMode]?.reassign}</span>
                    </button>
                  </Tooltip>
                  <Tooltip text={translations[langMode]?.unassignn} position="top">
                    <button
                      onClick={handleRemoveAssignee}
                      disabled={!canUpdateTask}
                      className={taskActionClass(canUpdateTask)}
                    >
                      <FiUserMinus size={16} />
                      <span className="hidden lg:inline">{translations[langMode]?.unassignn}</span>
                    </button>
                  </Tooltip>
                  <Tooltip text={translations[langMode]?.delete} position="top">
                    <button data-button-intent="danger"
                      onClick={handleDelete}
                      disabled={!canDeleteTask}
                      className={taskActionClass(
                        canDeleteTask,
                        "text-gray-700 hover:bg-red-50 hover:text-red-600"
                      )}
                    >
                      <Trash2 size={16} />
                      <span className="hidden lg:inline">{translations[langMode]?.delete}</span>
                    </button>
                  </Tooltip>
                </div>
              </div>
            )}

            <DrawControl
              markers={visibleMarkers}
              drawingEnabled={drawingEnabled}
              selectedOrgId={selectedOrgId}
              drawnItems={drawnItems}
              onShapeDrawn={handleShapeDrawn}
              isRtl={isRtl}
              isMobileViewport={isMobileViewport}
            />
          </MapContainer>

          {/* Detail Panel */}
          <div
            className={`transition-all duration-300 ${
              showDetailPanel
                ? `w-full max-w-[400px] sm:w-[400px] opacity-100 absolute ${isRtl ? "left-0" : "right-0"} z-10 overflow-y-auto`
                : "w-0 opacity-0 overflow-hidden"
            } shadow-lg ${isRtl ? "border-r" : "border-l"} ${
              mapType === "satellite" ? "bg-[#fffffff2] backdrop-blur-md" : "bg-[#ffffffcc]/80 backdrop-blur-md"
            }`}
            style={{
              top: showDatePanelInMap ? `${datePanelOffset}px` : "0px",
              height: showDatePanelInMap ? `calc(100% - ${datePanelOffset}px)` : "100%",
            }}
          >
            {showDetailPanel && (
              <DetailPanel
                selectedLocation={selectedLocation}
                selectedTaskDetail={selectedTaskDetail}
                isTaskDetailLoading={isTaskDetailLoading}
                onClose={handleClosePanel}
                onClear={handleClearLocation}
                onRefresh={handleRefresh}
                allTasks={visibleMarkers}
                onAgentClick={handleAgentClick}
                selectedAgentFilterId={selectedAssigneeId}
                selectedAgentId={selectedLocation?.agentId}
                selectedAgentName={selectedLocation?.agent}
                isBottomPanelOpen={showBottomPanel}
                bottomPanelExpanded={bottomPanelExpanded}
                showDatePanel={showDatePanelInMap}
              />
            )}
          </div>
          
        </div>

        {/* Assign Modal (for Active task at selected location) */}
        {showAssignModal && (
          <AssignAgentModal
            title={translations[langMode]?.assign ?? "Assign Task"}
            agents={availableAgents}
            groups={availableGroups}
            selectedAgentIds={selectedAgentIds}
            onToggleAgent={toggleAgentSelection}
            selectedGroupId={selectedGroupId}
            onSelectGroup={setSelectedGroupId}
            onConfirm={handleAssignConfirm}
            onClose={() => setShowAssignModal(false)}
            loading={actionLoading}
            langMode={langMode}
          />
        )}

        {/* Reassign Modal */}
        {showReassignModal && (
          <AssignAgentModal
            title={translations[langMode]?.reassign ?? "Reassign Task"}
            agents={availableAgents}
            groups={availableGroups}
            selectedAgentIds={selectedAgentIds}
            onToggleAgent={toggleAgentSelection}
            selectedGroupId={selectedGroupId}
            onSelectGroup={setSelectedGroupId}
            onConfirm={handleReassignConfirm}
            onClose={() => setShowReassignModal(false)}
            loading={actionLoading}
            langMode={langMode}
          />
        )}

        {/* Unassign Modal */}
        {showUnassignModal && (
          <UnassignConfirmationModal
            assignees={assigneesToUnassign}
            groups={availableGroups}
            onClose={() => {
              setShowUnassignModal(false);
              setSelectedAssigneesToUnassign([]);
              setSelectedUnassignGroupId(null);
            }}
            onConfirm={handleUnassignConfirm}
            selectedAssignees={selectedAssigneesToUnassign}
            onToggleAssignee={handleToggleUnassignAssignee}
            onToggleAllAssignees={handleToggleAllUnassignAssignees}
            selectedGroupId={selectedUnassignGroupId}
            onSelectGroup={setSelectedUnassignGroupId}
            langMode={langMode}
          />
        )}

        {/* Delete Modal */}
        {showDeleteModal && (
          <DeleteTask
            deleteFunction={handleDeleteConfirm}
            onClose={() => setShowDeleteModal(false)}
            comment={deleteComment}
            setComment={setDeleteComment}
          />
        )}

        <CustomMarkerSettingsModal
          isOpen={showCustomMarkerModal}
          onClose={() => setShowCustomMarkerModal(false)}
          workflowId={workflowIdForTracking}
          langMode={langMode}
          theme={theme}
          onSaveSuccess={handleCustomMarkerSaveSuccess}
        />

        {showBottomPanel && (
          <BottomPanel
            isOpen={showBottomPanel}
            initialExpanded={bottomPanelExpanded}
            onToggle={(expanded) => {
              setBottomPanelExpanded(expanded);
            }}
            selectedFieldAgentId={selectedAssigneeId}
            onFieldAgentChange={handleAnalyticsFieldAgentChange}
            onAnalyticsStatusChange={handleAnalyticsStatusChange}
            showDetailPanel={showDetailPanel}
            mapType={mapType}
            setActivityAnalyticsDate={handleAnalyticsDateChange}
          />
        )}

        {/* download Modal */}
        <Modal
          setDownloadModal={setDownloadModal}
          downloadModal={downloadModal}
          downloadFiltration={downloadFiltration}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          isLogoLoading={true}
        />
      </div>
    </div>
  );
};

export default MapView;
